import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// PrimeReact components
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { FilterMatchMode } from 'primereact/api';
import { Menu } from 'primereact/menu';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dropdown } from 'primereact/dropdown';

// Context and services
import { useScenarioContext, scenarioActions } from '../../context/ScenarioContext';
import ApiService, { handleApiError } from '../../services/api';
import { useExportScenarios } from '../../hooks/useExportScenarios';
import { usePracticeData } from '../../hooks/usePracticeData';

// Types
import { ScenarioResult, ScenarioSummary, PayerMixItem, ProductFamily, PayerProfitabilityItem, RebateDetail } from '../../types';

// Styles
import './ScenarioResults.css';
import { Tooltip } from 'primereact/tooltip';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { Tag } from 'primereact/tag';
import { InputNumber } from 'primereact/inputnumber';
import ScenarioChart from '../ScenarioChart/ScenarioChart';
import QtdDistributionChart from '../ScenarioChart/QtdDistributionChart';
import ScenarioExpert from '../ScenarioChart/ScenarioExpert';
import RebateDetailDialog from '../../components/RebateDetailDialog/RebateDetailDialog';


type GroupedScenarioResult = {
  scenario_id: number;
  summary?: ScenarioSummary;
  scenarioKind?: string;
  payerAssignments?: Record<string, string>;
  parentScenarioId?: number;
  totalNCR: number;
  totalWeightedNCR?: number;
  totalRebate: number;
  totalMedicareReimb: number;
  totalPvtReimb: number;
  totalPrice: number;
  distributionBreakdown: { family: string; color: string; percent: number }[];
  details: ScenarioResult[];
};

interface AggregatedScenarioResult {
  scenario_id: number;
  totalInvoiceUnits: number;
  totalNCR: number;
  totalWeightedNCR: number;
  total_rebate: number;
  estimated_medicare_reimbursement: number;
  estimated_pvt_reimbursement: number;
  net_price: number;
  totalPrice: number;
}

// Reserved scenario_id for the "current mix at the new volume" reference row
// (must match SCALED_CURRENT_SCENARIO_ID in the backend scenario_service).
const SCALED_CURRENT_SCENARIO_ID = 9999;

const ScenarioResults: React.FC = () => {
  const { state, dispatch } = useScenarioContext();
  const { isDemoMode } = usePracticeData();
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const categoryContainerRef = useRef<HTMLDivElement | null>(null);
  const exportMenuRef = useRef<Menu | null>(null);
  const modalExportMenuRef = useRef<Menu | null>(null);

  const [isVerifyingAccess, setIsVerifyingAccess] = useState(false);


  // State
  // Scenario filter removed from UI; keep state if future needed (currently unused)
  // const [selectedScenario, setSelectedScenario] = useState<number | null>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [chartOptions, setChartOptions] = useState<any>(null);
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const { isExporting, handleExport, handleExportWithPayerAssignments } = useExportScenarios({ filenamePrefix: 'scenario_results', toastRef: toast });
  // Removed showRanking toggle; ranking tags always shown when rank exists
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});
  const [selectedForComparison, setSelectedForComparison] = useState<GroupedScenarioResult[]>([]);
  const [isComparisonModalVisible, setComparisonModalVisible] = useState(false);
  const [isDecisionLogicModalVisible, setDecisionLogicModalVisible] = useState(false);
  const [decisionLogicScenario, setDecisionLogicScenario] = useState<GroupedScenarioResult | null>(null);
  const [aggregatedResults, setAggregatedResults] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  // Toggle between viewing Medicare reimbursement + NCR vs Weighted reimbursement + Weighted NCR
  const [ncrMode, setNcrMode] = useState<'medicare' | 'weighted'>('medicare');
  // Toggle between per-category-unit (EU) and per-invoice-unit (IU) figures. Always opens on category.
  const [unitMode, setUnitMode] = useState<'category' | 'invoice'>('category');
  const [isToolbarStacked, setIsToolbarStacked] = useState(false);
  const [generationBannerDismissed, setGenerationBannerDismissed] = useState(false);
  const [excludedPayerBannerDismissed, setExcludedPayerBannerDismissed] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    // Initialize from localStorage cache - this is just UI state for persistence
    try {
      const cached = localStorage.getItem('scenarioiq_favorites_ui_cache');
      if (cached) {
        const parsedCache = new Set<string>(JSON.parse(cached) as string[]);
        return parsedCache;
      }
    } catch (error) {
      console.error('Failed to load favorites from cache');
    }
    return new Set<string>();
  });
  // Payer mix stored per category; payerMix and payerMixAvailable are derived from selectedCategory
  const [payerMixByCategory, setPayerMixByCategory] = useState<Record<string, PayerMixItem[]>>({});
  const fetchedCategoryMixRef = useRef<Set<string>>(new Set());

  // Drug ↔ payer eligibility (payer preferences)
  const [drugPayerMatrix, setDrugPayerMatrix] = useState<Record<string, Record<string, boolean>>>({});
  const [payerPrefsLoading, setPayerPrefsLoading] = useState(false);
  const [payerPrefsError, setPayerPrefsError] = useState<string | null>(null);

  // Product family list for the category (used for alternate scenario dropdown options)
  const [productFamiliesByCategory, setProductFamiliesByCategory] = useState<Record<string, ProductFamily[]>>({});
  const [productFamiliesLoading, setProductFamiliesLoading] = useState(false);
  const [productFamiliesError, setProductFamiliesError] = useState<string | null>(null);

  // Expanded payer rows inside Decision logic (per scenario_id)
  const [expandedDecisionPayers, setExpandedDecisionPayers] = useState<Record<number, Record<string, boolean>>>({});

  // Per-payer profitability (fee-schedule reimbursement based) — computed inline by the
  // generation endpoints and persisted in context + localStorage like results/assignments.
  const profitabilityByScenarioId = state.payerProfitabilityByScenarioId ?? {};

  // Rebate drill-down rows. Persisted trimmed and capped, so they survive a refresh.
  // Memoized so the `?? []` fallback doesn't hand the hooks below a fresh array each render.
  const rebateDetails = useMemo(() => state.rebateDetails ?? [], [state.rebateDetails]);
  const [rebateDetailTarget, setRebateDetailTarget] = useState<{ scenarioId: number; pfId: string; pfLabel: string } | null>(null);
  // Separate from the target so closing can animate out with its content still mounted.
  const [rebateDetailVisible, setRebateDetailVisible] = useState(false);
  const rebateDetailRows = useMemo(() => {
    if (!rebateDetailTarget) return [];
    return rebateDetails.filter(d =>
      Number(d.scenario_id) === rebateDetailTarget.scenarioId &&
      String(d.product_family_id) === String(rebateDetailTarget.pfId)
    );
  }, [rebateDetails, rebateDetailTarget]);

  // Scoped to the result rows an export carries, so the sheet matches the one beside it.
  const rebateDetailsForExport = useCallback((data: ScenarioResult[]): RebateDetail[] => {
    if (rebateDetails.length === 0 || data.length === 0) return [];
    const wanted = new Set(
      data.map(r => `${Number(r.scenario_id)}:${String(r.product_family_id)}`)
    );
    return rebateDetails.filter(d =>
      wanted.has(`${Number(d.scenario_id)}:${String(d.product_family_id)}`)
    );
  }, [rebateDetails]);


  // Presence decides whether the drill-down link renders; the flag picks the tooltip wording.
  // Families with no contract still come back from the SP as placeholder rows, so only a real
  // contract row or a manual adjustment counts.
  const pfRebateEarning = useMemo(() => {
    const byPf = new Map<string, boolean>();
    rebateDetails.forEach(d => {
      if (!d.has_contract && d.rebate_source !== 'Excluded') return;
      const key = `${Number(d.scenario_id)}:${String(d.product_family_id)}`;
      byPf.set(key, (byPf.get(key) ?? false) || Number(d.rebate_earned || 0) > 0);
    });
    return byPf;
  }, [rebateDetails]);

  // Alternate scenario mode (Decision logic modal)
  const [alternateScenarioId, setAlternateScenarioId] = useState<number | null>(null);
  const [alternateModeEnabled, setAlternateModeEnabled] = useState(false);
  const [alternatePayerAssignments, setAlternatePayerAssignments] = useState<Record<string, string>>({});
  const [isCalculatingAlternate, setIsCalculatingAlternate] = useState(false);
  const [alternatePreviewExpanded, setAlternatePreviewExpanded] = useState(false);
  // Pinned ordering for user-run alternate scenarios — stored in context so it survives navigation
  const pinnedAlternateScenarioIds = state.pinnedAlternateScenarioIds ?? [];
  const setPinnedAlternateScenarioIds = (updater: number[] | ((prev: number[]) => number[])) => {
    const next = typeof updater === 'function' ? updater(pinnedAlternateScenarioIds) : updater;
    dispatch(scenarioActions.setPinnedAlternateScenarioIds(next));
  };

  // Legacy alternate scenario modal (distribution-based, no payer assignments)
  const [isLegacyAlternateModalVisible, setLegacyAlternateModalVisible] = useState(false);
  const [legacyAlternateScenario, setLegacyAlternateScenario] = useState<GroupedScenarioResult | null>(null);
  const [legacyAlternateDistributions, setLegacyAlternateDistributions] = useState<Record<string, number>>({});
  const [isCalculatingLegacyAlternate, setIsCalculatingLegacyAlternate] = useState(false);

  // Derived from selectedCategory — updates automatically when the category pill changes
  const payerMix = useMemo(
    () => (selectedCategory ? (payerMixByCategory[selectedCategory] ?? []) : []),
    [payerMixByCategory, selectedCategory]
  );
  const payerMixAvailable = payerMix.length > 0;
  // True once the payer-mix fetch for the selected category has resolved (any
  // outcome). The units toggle is gated on this so it appears together with the
  // async weighted-NCR toggle instead of rendering ahead of it. When there's no
  // category to fetch, treat it as loaded so the units toggle still shows.
  const payerMixLoaded = !selectedCategory || (selectedCategory in payerMixByCategory);

  // UI-only alternate numbering (scenario_id stays an int everywhere)
  const altIndexByScenarioId = useMemo(() => {
    const map = new Map<number, number>();
    pinnedAlternateScenarioIds.forEach((sid, idx) => map.set(Number(sid), idx + 1));
    return map;
  }, [pinnedAlternateScenarioIds]);

  // Resolve the modeled category-size delta (%) for the scaled-current row.
  // Prefer the factor the user set on the generator; if that isn't available
  // (e.g. after a reload where the generator form state was cleared), derive it
  // from the data: scaled-current units vs current units for the selected category.
  const getScaledCurrentPct = (): number => {
    const mapFactor = state.generatorState?.form?.category_scale_factor_map?.[selectedCategory ?? ''];
    if (mapFactor != null && Math.abs(mapFactor - 1) > 1e-9) {
      return Math.round((mapFactor - 1) * 100);
    }
    const inCat = (r: ScenarioResult) => !selectedCategory || r.category === selectedCategory;
    const sumUnits = (sid: number) =>
      state.scenarioResults
        .filter((r) => Number(r.scenario_id) === sid && inCat(r))
        .reduce((s, r) => s + Number(r.category_units || 0), 0);
    const base = sumUnits(0);
    const scaled = sumUnits(SCALED_CURRENT_SCENARIO_ID);
    return base > 0 && scaled > 0 ? Math.round((scaled / base - 1) * 100) : 0;
  };

  const getScenarioDisplayLabel = (scenarioId: number) => {
    if (scenarioId === 0) return 'Current';
    // Reserved id for the "current mix at the new volume" reference (backend
    // SCALED_CURRENT_SCENARIO_ID). Always show the modeled delta, e.g. "Current @ +50%".
    // The backend only injects this row when the factor is off baseline, so pct is non-zero.
    if (scenarioId === SCALED_CURRENT_SCENARIO_ID) {
      const pct = getScaledCurrentPct();
      return `Current @ ${pct > 0 ? '+' : ''}${pct}%`;
    }
    const altIdx = altIndexByScenarioId.get(scenarioId);
    return altIdx != null ? `Alt ${altIdx}` : `Scenario ${scenarioId}`;
  };

  // Sync state.currentUser with the account number from the actual results data.
  // The results carry `logged_in_user` (the account number used for generation),
  // which must match what we send in API calls (favorites, payer mix, etc.).
  useEffect(() => {
    if (state.scenarioResults.length > 0) {
      const resultsAccountNumber = state.scenarioResults[0]?.logged_in_user;
      if (resultsAccountNumber && resultsAccountNumber !== state.currentUser) {
        dispatch(scenarioActions.setCurrentUser(resultsAccountNumber));
      }
      // Reset banner dismiss flags on new generation
      setGenerationBannerDismissed(false);
      setExcludedPayerBannerDismissed(false);
    }
  }, [state.scenarioResults]);

  // Clear per-category payer mix cache whenever the logged-in user changes
  useEffect(() => {
    fetchedCategoryMixRef.current = new Set();
    setPayerMixByCategory({});
  }, [state.currentUser]);

  // Lazily fetch payer mix for the selected category and cache it for the session
  useEffect(() => {
    if (!state.currentUser || !selectedCategory) return;
    const cacheKey = `${state.currentUser}:${selectedCategory}`;
    if (fetchedCategoryMixRef.current.has(cacheKey)) return;
    fetchedCategoryMixRef.current.add(cacheKey);

    let cancelled = false;
    const doFetch = async () => {
      try {
        const resp = await ApiService.getPayerMixByCategory({ logged_in_user: state.currentUser!, category: selectedCategory });
        const mix = ((resp as any)?.mix ?? []) as PayerMixItem[];
        if (!cancelled) {
          setPayerMixByCategory(prev => ({ ...prev, [selectedCategory]: mix }));
        }
      } catch {
        if (!cancelled) {
          setPayerMixByCategory(prev => ({ ...prev, [selectedCategory]: [] }));
          fetchedCategoryMixRef.current.delete(cacheKey);
        }
      }
    };
    doFetch();
    return () => { cancelled = true; };
  }, [state.currentUser, selectedCategory]);

  // Fetch payer prefs for the selected category (same pattern as Generator page)
  useEffect(() => {
    const userId = state.currentUser;
    const category = selectedCategory;
    if (!userId || !category) {
      setDrugPayerMatrix({});
      setPayerPrefsLoading(false);
      setPayerPrefsError(null);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setPayerPrefsLoading(true);
      setPayerPrefsError(null);
      try {
        const resp = await ApiService.getDrugPayerMatrix(userId, category);
        const matrix = (resp as any)?.drugPayerMatrix || (resp as any)?.preferences || {};
        if (!cancelled) setDrugPayerMatrix(matrix);
      } catch (e) {
        if (!cancelled) {
          setDrugPayerMatrix({});
          setPayerPrefsError(handleApiError(e));
        }
      } finally {
        if (!cancelled) setPayerPrefsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [state.currentUser, selectedCategory]);

  // Fetch all product families for the selected category (names-all), to populate alternate dropdown options.
  useEffect(() => {
    const userId = state.currentUser;
    const category = selectedCategory;
    if (!userId || !category) {
      setProductFamiliesLoading(false);
      setProductFamiliesError(null);
      return;
    }

    let cancelled = false;
    const run = async () => {
      // Avoid refetch if we already have it
      if (productFamiliesByCategory[category]?.length) return;

      setProductFamiliesLoading(true);
      setProductFamiliesError(null);
      try {
        const resp = await ApiService.getCategoryProductFamiliesNames(userId, category);
        const families = (resp as any)?.data?.product_families ?? (resp as any)?.product_families ?? [];
        if (!cancelled) {
          setProductFamiliesByCategory((prev) => ({ ...prev, [category]: families as ProductFamily[] }));
        }
      } catch (e) {
        if (!cancelled) {
          setProductFamiliesError(handleApiError(e));
        }
      } finally {
        if (!cancelled) setProductFamiliesLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentUser, selectedCategory]);

  // Per-payer profitability is computed server-side and returned with the generation
  // response (ScenarioResultsResponse.profitability), then merged into context by the
  // generator (batch) and the alternate recalc below. No separate fetch is needed here.

  // No client-side recalculation; use backend-provided fields only.

  const categoryOptions = Array.from(new Set(state.scenarioResults.map(r => r.category))).map(cat => ({
    label: cat,
    value: cat
  }));

  // Generate a unique signature for a complete scenario (all product families together)
  const getScenarioSignature = (scenarioDetails: ScenarioResult[]) => {
    // Sort by product_family_id to ensure consistent ordering
    const sorted = [...scenarioDetails].sort((a, b) => 
      String(a.product_family_id).localeCompare(String(b.product_family_id))
    );
    // Create a signature from the distribution and invoice units of each product family
    const signature = {
      logged_in_user: sorted[0]?.logged_in_user,
      category: sorted[0]?.category,
      distributions: sorted.map(detail => ({
        product_family_id: detail.product_family_id,
        future_distribution: detail.future_distribution,
        invoice_units: detail.invoice_units
      }))
    };
    return JSON.stringify(signature);
  };

  // Clear favorites cache only when new scenarios are actually generated
  useEffect(() => {
    // Only clear cache when generation just finished (transitioned from true to false)
    const wasGenerating = sessionStorage.getItem('was_generating') === 'true';
    const isGenerating = state.generationStatus?.inProgress === true;
    const completedCategories = state.generationStatus?.completedCategories ?? 0;
    
    if (wasGenerating && !isGenerating && completedCategories > 0) {
      try {
        localStorage.removeItem('scenarioiq_favorites_ui_cache');
        setFavorites(new Set());
      } catch (error) {
        console.error('Failed to clear favorites cache');
      }
    }
    
    // Track generation state
    sessionStorage.setItem('was_generating', isGenerating ? 'true' : 'false');
  }, [state.generationStatus?.inProgress, state.generationStatus?.completedCategories]);

  const toggleFavorite = async (scenario: GroupedScenarioResult) => {
    if (scenario.details.length === 0) return;

    // Demo accounts are read-only server-side (423). Stop here rather than
    // letting the optimistic update flip the star and then roll back.
    if (isDemoMode) {
      toast.current?.show({
        severity: 'info',
        summary: 'Read-only demo',
        detail: 'Favorites cannot be saved from a demo account.',
        life: 3000,
      });
      return;
    }
    
    // Generate signature for the complete scenario (all product families)
    const signature = getScenarioSignature(scenario.details);
    const isFavorite = favorites.has(signature);
    
    // Create new set with the change applied
    const newFavorites = new Set(favorites);

    if (isFavorite) {
      // Removing favorite - remove the scenario signature
      newFavorites.delete(signature);
      // Update UI immediately for responsiveness
      setFavorites(newFavorites);
      
      // Update localStorage cache immediately for UI persistence
      try {
        localStorage.setItem('scenarioiq_favorites_ui_cache', JSON.stringify(Array.from(newFavorites)));
      } catch (error) {
        console.error('Failed to update favorites cache');
      }
      
      try {
        // Remove updated_by if present to prevent API issues
        const resultsToUpdate = scenario.details.map(d => {
          const { updated_by, ...rest } = d as ScenarioResult & { updated_by?: unknown };
          return { ...rest, is_active: false };
        });
        
        await ApiService.updateFavoriteScenario({
          user_id: state.currentUser || '',
          category: selectedCategory || scenario.details[0].category,
          scenario_results: resultsToUpdate
        });
        
        toast.current?.show({
          severity: 'info',
          summary: 'Favorite Removed',
          detail: `${getScenarioDisplayLabel(Number(scenario.scenario_id))} removed from favorites`,
          life: 2000,
        });
      } catch (error) {
        console.error('Failed to remove favorite:', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to remove favorite scenario',
          life: 3000,
        });
        // Revert by adding it back
        const revertedFavorites = new Set(newFavorites);
        revertedFavorites.add(signature);
        setFavorites(revertedFavorites);
        // Update localStorage cache to match reverted state
        try {
          localStorage.setItem('scenarioiq_favorites_ui_cache', JSON.stringify(Array.from(revertedFavorites)));
        } catch (error) {
          console.error('Failed to update favorites cache');
        }
      }
    } else {
      // Adding favorite - add the scenario signature
      newFavorites.add(signature);
      // Update UI immediately for responsiveness
      setFavorites(newFavorites);
      
      // Update localStorage cache immediately for UI persistence
      try {
        localStorage.setItem('scenarioiq_favorites_ui_cache', JSON.stringify(Array.from(newFavorites)));
      } catch (error) {
        console.error('Failed to update favorites cache');
      }
      
       try {
         const payerAssignmentsToPersist =
           scenario.payerAssignments && Object.keys(scenario.payerAssignments).length > 0
             ? scenario.payerAssignments
             : undefined;

         // Remove updated_by if present to prevent API issues
         const resultsToSave = scenario.details.map(d => {
           const { updated_by, ...rest } = d as ScenarioResult & { updated_by?: unknown };
           return {
             ...rest,
             is_active: true,
             ...(payerAssignmentsToPersist ? { payer_assignments: payerAssignmentsToPersist } : {}),
           };
         });
         
         // Persist this scenario's drill-down rows (including no-contract placeholders,
         // which let Favorites tell "no contracts" apart from "nothing saved") so the
         // rebate detail dialog works there without regenerating.
         const rebateDetailsToPersist = rebateDetails.filter(
           d => Number(d.scenario_id) === Number(scenario.scenario_id)
         );

         await ApiService.saveFavoriteScenario({
           user_id: state.currentUser || '',
           category: selectedCategory || scenario.details[0].category,
           scenario_results: resultsToSave,
           ...(payerAssignmentsToPersist ? { payer_assignments: payerAssignmentsToPersist } : {}),
           ...(rebateDetailsToPersist.length > 0 ? { rebate_details: rebateDetailsToPersist } : {}),
         });         toast.current?.show({
            severity: 'success',
            summary: 'Favorite Saved',
            detail: `${getScenarioDisplayLabel(Number(scenario.scenario_id))} saved to favorites`,
            life: 2000,
         });
      } catch (error) {
        console.error('Failed to save favorite:', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save favorite scenario',
          life: 3000,
        });
        // Revert by removing it
        const revertedFavorites = new Set(newFavorites);
        revertedFavorites.delete(signature);
        setFavorites(revertedFavorites);
        // Update localStorage cache to match reverted state
        try {
          localStorage.setItem('scenarioiq_favorites_ui_cache', JSON.stringify(Array.from(revertedFavorites)));
        } catch (error) {
          console.error('Failed to update favorites cache');
        }
      }
    }
  };

  // Check if the user still has access to the organization of the currently viewed results
  useEffect(() => {
    const checkAccessAndClear = async () => {
      if (state.scenarioResults.length > 0 && state.currentOrg) {
        try {
          setIsVerifyingAccess(true);
          const response = await ApiService.getUserPractices();
          
          const corporatePracticeMapping = response.data?.corporate_practice_mapping;
          if (corporatePracticeMapping && corporatePracticeMapping.length > 0) {
            setIsVerifyingAccess(false);
            return;
          }
          
          const practiceAccountNumbers = response.data?.practice_account_numbers || [];
          const corporateAccountNumber = response.data?.corporate_account_number;
          if (state.currentUser && (practiceAccountNumbers.includes(state.currentUser) || corporateAccountNumber === state.currentUser)) {
            setIsVerifyingAccess(false);
            return;
          }
          
          const practices = response.data?.practice_info || [];
          const displayNames: string[] = [];
          
          // Check if corporate parent exists
          const hasCorporateParent = response.data?.corporate_name && 
                                     response.data?.corporate_account_number &&
                                     response.data.corporate_name.trim() !== '' &&
                                     response.data.corporate_account_number.trim() !== '';
          
          if (hasCorporateParent) {
            // Add corporate parent
            displayNames.push(response.data.corporate_name.trim());
            // Add practices (trimmed, no indentation for comparison)
            practices.forEach((practice) => {
              const name = (practice.displayName || practice.name || 'Unknown').trim();
              displayNames.push(name);
            });
          } else {
            // Flat list
            practices.forEach((practice) => {
              const name = (practice.displayName || practice.name || 'Unknown').trim();
              displayNames.push(name);
            });
          }

          // If the org from the results is not in the user's current list of accessible orgs
          if (!displayNames.includes(state.currentOrg.trim())) {
            dispatch(scenarioActions.clearAll());
          }
        } catch (error) {
          console.error('Failed to verify organization access:', error);
          toast.current?.show({
            severity: 'error',
            summary: 'Verification Failed',
            detail: 'Could not verify access to organizations.',
            life: 4000,
          });
        } finally {
          setIsVerifyingAccess(false);
        }
      }
    };

  }, []); // Run only once on component mount

  // When switching categories, automatically collapse any expanded scenario detail rows
  useEffect(() => {
    let changed = false;
    if (Object.keys(expandedRows).length > 0) {
      setExpandedRows({});
      changed = true;
    }
    // Clear comparison selections to avoid mixing scenarios across categories
    if (selectedForComparison.length > 0) {
      setSelectedForComparison([]);
      changed = true;
    }
    if (changed) {
      // Optionally could toast or log; keeping silent for UX simplicity
    }
  }, [selectedCategory]);

  // When category options change, ensure a valid category is selected.
  useEffect(() => {
    const categoryValues = categoryOptions.map(c => c.value);
    // If there's no selected category or the selected one is no longer valid,
    // and there are available categories, select the first one.
    if ((!selectedCategory || !categoryValues.includes(selectedCategory)) && categoryValues.length > 0) {
      setSelectedCategory(categoryValues[0]);
    }
  }, [categoryOptions, selectedCategory]);

  // Sync locally selected category to global context state.currentCategory
  useEffect(() => {
    if (selectedCategory) {
      dispatch(scenarioActions.setCurrentCategory(selectedCategory));
    }
  }, [selectedCategory, dispatch]);

  // Detect when category pills wrap to additional lines so we can stack right toolbar underneath.
  useEffect(() => {
    const detectWrap = () => {
      const el = categoryContainerRef.current;
      if (!el) return;
      // Approximate line height via first child or computed style.
      const firstChild = el.children[0] as HTMLElement | undefined;
      const lineHeight = firstChild ? firstChild.offsetHeight : 30; // fallback
      // If the scrollHeight is more than ~1.4x a single line, assume wrapping.
      const wrapped = el.scrollHeight > lineHeight * 1.4; // heuristic
      setIsToolbarStacked(wrapped);
    };
    detectWrap();
    window.addEventListener('resize', detectWrap);
    return () => window.removeEventListener('resize', detectWrap);
  }, [categoryOptions]);

  useEffect(() => {
    if (state.scenarioResults && state.scenarioResults.length > 0) {
      const agg = aggregateByScenarioId(state.scenarioResults);
      setAggregatedResults(agg);
    }
  }, [state.scenarioResults]);

  const aggregateByScenarioId = (results: any[]) => {
    const grouped = results.reduce((acc, row) => {
      if (!acc[row.scenario_id]) {
        acc[row.scenario_id] = {
          scenario_id: row.scenario_id,
          // totalContractUnits: 0, // keep if you need it, else remove
          totalInvoiceUnits: 0, // keep if you need it, else remove
          totalNCR: 0, 
          totalWeightedNCR: 0, // optional, remove if not needed
          total_rebate: 0,
          estimated_medicare_reimbursement: 0,
          estimated_pvt_reimbursement: 0,
          // Preserve original accumulated net_price * category_units for backward compatibility (deprecated for chart)
          net_price: 0,
          // New explicit totalPrice aggregated across rows using invoice_units (preferred)
          totalPrice: 0,
        };
      }
      // acc[row.scenario_id].totalContractUnits += Number(row.contract_units || 0);
      acc[row.scenario_id].totalInvoiceUnits += Number(row.category_units || 0);
      acc[row.scenario_id].totalNCR += Number(row.total_estimated_ncr || 0);
      // acc[row.scenario_id].totalWeightedNCR += Number(row.total_weighted_ncr || 0);
      acc[row.scenario_id].totalWeightedNCR += Number(row.total_weighted_ncr || 0);
      acc[row.scenario_id].total_rebate += Number(row.total_rebate || 0);
      acc[row.scenario_id].estimated_medicare_reimbursement += Number(row.estimated_medicare_reimbursement || 0);
      acc[row.scenario_id].estimated_pvt_reimbursement += Number(row.estimated_pvt_reimbursement || 0);
      // Legacy accumulated value (may have used category_units historically)
      // acc[row.scenario_id].net_price += Number(row.net_price || 0) * Number(row.category_units || 0);
      acc[row.scenario_id].net_price += Number(row.net_price || 0);

      // Preferred total price uses invoice_units; fall back to category_units if invoice_units absent
      const unitsForPrice = Number(row.invoice_units ?? row.category_units ?? 0);
      acc[row.scenario_id].totalPrice += Number(row.net_price || 0) * unitsForPrice;
      return acc;
    }, {} as Record<number, any>);

    return Object.values(grouped);
  };

  const sortScenarioIdsWithPinnedAlternates = (scenarioIdA: number, scenarioIdB: number) => {
    const idxA = pinnedAlternateScenarioIds.indexOf(scenarioIdA);
    const idxB = pinnedAlternateScenarioIds.indexOf(scenarioIdB);
    const inPinnedA = idxA >= 0;
    const inPinnedB = idxB >= 0;

    if (inPinnedA && inPinnedB) return idxB - idxA;
    if (inPinnedA && !inPinnedB) return -1;
    if (!inPinnedA && inPinnedB) return 1;

    // Current (scaled) sits immediately after the baseline (Current) and before
    // the modeled scenarios, despite its large reserved id.
    const rank = (id: number) => (id === SCALED_CURRENT_SCENARIO_ID ? 0.5 : id);
    return rank(scenarioIdA) - rank(scenarioIdB);
  };

  // Custom sort for DataTable columns: alternates always pinned at top, rest sorted by column value
  const pinnedSort = (e: any) => {
    const { data, field, order: sortOrder } = e;
    const order = sortOrder ?? 1;
    const pinned = data.filter((r: any) => pinnedAlternateScenarioIds.includes(r.scenario_id));
    const rest = data.filter((r: any) => !pinnedAlternateScenarioIds.includes(r.scenario_id));

    pinned.sort((a: any, b: any) => {
      const iA = pinnedAlternateScenarioIds.indexOf(a.scenario_id);
      const iB = pinnedAlternateScenarioIds.indexOf(b.scenario_id);
      return iB - iA;
    });

    rest.sort((a: any, b: any) => {
      const valA = a[field];
      const valB = b[field];
      if (valA == null && valB != null) return -1 * order;
      if (valA != null && valB == null) return 1 * order;
      if (valA == null && valB == null) return 0;
      if (typeof valA === 'string' && typeof valB === 'string')
        return valA.localeCompare(valB) * order;
      return (valA < valB ? -1 : valA > valB ? 1 : 0) * order;
    });

    return [...pinned, ...rest];
  };

  // Neither the modelling SP nor the grouping below imposes an order on product families, and
  // SQL Server guarantees none without an ORDER BY — so rows could come back in a different
  // order between runs, which makes scenarios hard to compare and reads as if the data changed.
  // Ordering here rather than at each consumer keeps the table, the expanded breakdown and both
  // export paths (all results / selected scenarios) in agreement.
  const orderedScenarioResults = useMemo(() => (
    [...state.scenarioResults].sort((a, b) =>
      sortScenarioIdsWithPinnedAlternates(Number(a.scenario_id), Number(b.scenario_id)) ||
      (a.product_family_name ?? '').localeCompare(b.product_family_name ?? '', undefined, { sensitivity: 'base' }))
  ), [state.scenarioResults, pinnedAlternateScenarioIds]);

  // Memoize aggregated scenario data so the chart only receives a stable reference unless inputs change
  const aggregatedScenarioData = useMemo(() => {
    const source = selectedCategory
      ? orderedScenarioResults.filter(r => r.category === selectedCategory)
      : orderedScenarioResults;
    const agg = aggregateByScenarioId(source) as any[];
    return [...agg].sort((a, b) => sortScenarioIdsWithPinnedAlternates(Number(a.scenario_id), Number(b.scenario_id)));
  }, [orderedScenarioResults, selectedCategory, pinnedAlternateScenarioIds]);

  // Data restricted to selected category for exports and other category-specific operations
  const selectedCategoryResults = useMemo(() => {
    return selectedCategory
      ? orderedScenarioResults.filter(r => r.category === selectedCategory)
      : [];
  }, [orderedScenarioResults, selectedCategory]);

  // True when every product family in the selected category is missing a fee
  // schedule — weighted NCR would just repeat Medicare NCR, so the toggle is
  // disabled. Stays false until results have loaded (so the button is enabled
  // by default and only disabled once we can confirm the full set is missing).
  const allFeeSchedulesMissing = useMemo(() => {
    if (selectedCategoryResults.length === 0) return false;
    const missingSet = new Set(
      (state.missingFeeSchedulePfIds?.[selectedCategory ?? ''] ?? []).map(String)
    );
    return selectedCategoryResults.every(r => missingSet.has(String(r.product_family_id)));
  }, [selectedCategoryResults, state.missingFeeSchedulePfIds, selectedCategory]);

  // Weighted NCR is not applicable for oral inventory categories.
  const categoryIsOral = useMemo(() => {
    if (!selectedCategory) return false;
    return (productFamiliesByCategory[selectedCategory] ?? []).some(
      pf => (pf.inventory_type ?? '').trim().toLowerCase() === 'oral'
    );
  }, [selectedCategory, productFamiliesByCategory]);

  // Oral categories use benchmark (not Medicare) reimbursement terminology.
  const ncrLabel = categoryIsOral ? 'Benchmark' : 'Medicare';

  // Per-unit basis derived from the unit toggle.
  const perUnitLabel = unitMode === 'category' ? 'per EU' : 'per IU';
  const perUnitSuffix = unitMode === 'category' ? '_per_eu' : '_per_iu';
  const unitsField = unitMode === 'category' ? 'category_units' : 'invoice_units';
  const unitsHeader = unitMode === 'category' ? 'Category Units' : 'Invoice Units';

  // Ensure weighted mode is only available when payer mix is present
  useEffect(() => {
    if ((!payerMixAvailable || allFeeSchedulesMissing || categoryIsOral) && ncrMode === 'weighted') {
      setNcrMode('medicare');
    }
  }, [payerMixAvailable, allFeeSchedulesMissing, categoryIsOral, ncrMode]);

  // Shared ordered palette: a family's color is assigned by its category-unit rank
  // (largest share first), so the QTD chart and this page read as one cohesive ramp
  // and always agree on a family's color.
  //
  // Families that actually appear -- in the results or as a purchased QTD segment -- are
  // ranked ahead of the rest. A category can hold 15+ families (BCR-ABL), and ranking all
  // of them let `i % palette.length` wrap, so two families on screen shared a color.
  // Past the palette, colors are generated rather than reused.
  const familyColorMap = useMemo(() => {
    const palette = [
      "#1E3A5F", // navy
      "#2E6CA6", // blue
      "#2FA98E", // teal
      "#E0A93B", // amber
      "#8B6BB1", // purple
      "#C2685E", // clay
      "#5FA8D3", // sky
      "#6E8B3D", // olive
      "#B5739E", // mauve
      "#7A8CA3", // slate
    ];
    const norm = (name?: string | null) => (name || '').trim().toLowerCase();
    const families = productFamiliesByCategory[selectedCategory ?? ''] ?? [];

    const qtdRatio = new Map<string, number>();
    const shown = new Set<string>();
    families.forEach(pf => {
      const key = norm(pf.product_family_name);
      if (!key) return;
      qtdRatio.set(key, pf.product_family_to_category_qtd_ratio ?? 0);
      // Same "purchased" test the QTD chart uses to decide which segments it draws.
      if ((pf.product_family_to_category_qtd_ratio ?? 0) > 0
        || (pf.product_family_to_category_spend_qtd_ratio ?? 0) > 0
        || (pf.product_family_to_category_invoice_units_qtd_ratio ?? 0) > 0) {
        shown.add(key);
      }
    });
    selectedCategoryResults.forEach(r => {
      const key = norm(r.product_family_name);
      if (key) shown.add(key);
    });

    const ordered = Array.from(new Set([...Array.from(shown), ...Array.from(qtdRatio.keys())]))
      .sort((a, b) =>
        Number(shown.has(b)) - Number(shown.has(a))
        || (qtdRatio.get(b) ?? 0) - (qtdRatio.get(a) ?? 0)
        || a.localeCompare(b));

    // Golden-angle hue steps keep generated colors apart from each other.
    const extraColor = (n: number) => `hsl(${Math.round((n * 137.508 + 20) % 360)}, 45%, 45%)`;
    const map = new Map<string, string>();
    ordered.forEach((key, i) => {
      map.set(key, i < palette.length ? palette[i] : extraColor(i - palette.length));
    });
    return map;
  }, [productFamiliesByCategory, selectedCategory, selectedCategoryResults]);

  // Normalized key so casing/whitespace variants resolve to one color; unknown families fall back to gray.
  const getColorForFamily = useCallback(
    (familyName: string): string => familyColorMap.get(familyName.trim().toLowerCase()) ?? '#94A3B8',
    [familyColorMap]
  );


  const getDistributionBreakdown = (details: ScenarioResult[]) => {
    const distributionMap: Record<string, number> = {};

    for (const row of details) {
      const family = row.product_family_name || 'Other';
      distributionMap[family] = (distributionMap[family] || 0) + (row.future_distribution ?? 0);
    } 

    const total = Object.values(distributionMap).reduce((a, b) => a + b, 0) || 1;

    return Object.entries(distributionMap).map(([family, value]) => ({
      family,
      percent: value / total,
      color: getColorForFamily(family)
    }));
  };

  useEffect(() => {
    if (state.scenarioSummary.length > 0) {
      setupChartData();
    }
  }, [state.scenarioSummary, pinnedAlternateScenarioIds]);

  const setupChartData = () => {
    const topScenarios = state.scenarioSummary
      .filter(s => s.scenario_rank && s.scenario_rank <= 10)
      .sort((a, b) => (a.scenario_rank || 0) - (b.scenario_rank || 0));

    const data = {
      labels: topScenarios.map((s) => getScenarioDisplayLabel(Number(s.scenario_id))),
      datasets: [
        {
          label: 'Total NCR ($)',
          data: topScenarios.map(s => s.total_scenario_ncr),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 2,
          fill: false,
          tension: 0.4
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
        },
        title: {
          display: true,
          text: 'Top 10 Scenarios by NCR Performance'
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: function (value: any) {
              return '$' + value.toLocaleString();
            }
          }
        }
      }
    };

    setChartData(data);
    setChartOptions(options);
  };

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return 'N/A';
    return value.toLocaleString();
  };

  const formatPercentage = (value: number): string => {
    return Math.round(value * 100) + '%';
  };

  const normalizePayerName = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ');

  const drugPayerMatrixByNormalizedPayer = useMemo(() => {
    const out: Record<string, Record<string, boolean>> = {};
    Object.entries(drugPayerMatrix || {}).forEach(([productFamilyId, row]) => {
      const normalizedRow: Record<string, boolean> = {};
      Object.entries(row || {}).forEach(([payer, allowed]) => {
        normalizedRow[normalizePayerName(String(payer))] = (allowed as any) === true;
      });
      out[String(productFamilyId)] = normalizedRow;
    });
    return out;
  }, [drugPayerMatrix]);

  const computeDistributionFromAssignments = (params: {
    assignments: Record<string, string>;
    candidateProductFamilyIds: string[];
    payerItems: Array<{ payer: string; vol: number }>;
    baselineMix: Record<string, number>;
    minDistributionMap?: Record<string, number>;
  }): { distributionByProductFamilyId: Record<string, number>; totalAllocatedPercent: number } => {
    const {
      assignments,
      candidateProductFamilyIds,
      payerItems,
      baselineMix,
      minDistributionMap,
    } = params;

    // Backend-equivalent logic (build_distribution_from_assignments)
    // - payerItems.vol is expected to be on a 0..1 scale
    const pfVolume: Record<string, number> = {};
    candidateProductFamilyIds.forEach((pfId) => {
      pfVolume[String(pfId)] = 0;
    });

    // Normalize assignment keys to tolerate casing/spacing mismatches.
    const assignmentByNormalizedPayer: Record<string, string> = {};
    Object.entries(assignments || {}).forEach(([payer, pfId]) => {
      assignmentByNormalizedPayer[normalizePayerName(String(payer))] = String(pfId);
    });

    // Step 1: Add minimums first
    const minMap = minDistributionMap ?? {};
    candidateProductFamilyIds.forEach((pfIdRaw) => {
      const pfId = String(pfIdRaw);
      pfVolume[pfId] = (pfVolume[pfId] || 0) + Number(minMap[pfId] ?? 0);
    });

    // Step 2: Add assigned payer volumes
    let assignedTotal = 0;
    payerItems.forEach((p) => {
      const payerNorm = normalizePayerName(String(p.payer));
      const vol = Number(p.vol ?? 0);
      if (!vol || Number.isNaN(vol)) return;
      const assignedPfId = assignmentByNormalizedPayer[payerNorm];
      if (!assignedPfId) return;
      if (pfVolume[assignedPfId] === undefined) pfVolume[assignedPfId] = 0;
      pfVolume[assignedPfId] += vol;
      assignedTotal += vol;
    });

    // Step 3: Fill remaining flexible volume using baseline fractions
    const sumSoFar = candidateProductFamilyIds.reduce((sum, pfIdRaw) => sum + Number(pfVolume[String(pfIdRaw)] ?? 0), 0);
    const flexibleRemaining = Math.max(0, 1 - sumSoFar);

    const baselineFlexibleSum = candidateProductFamilyIds.reduce((sum, pfIdRaw) => {
      const pfId = String(pfIdRaw);
      return sum + Number(baselineMix?.[pfId] ?? 0);
    }, 0);

    if (baselineFlexibleSum > 0 && flexibleRemaining > 0) {
      candidateProductFamilyIds.forEach((pfIdRaw) => {
        const pfId = String(pfIdRaw);
        const baselineFraction = Number(baselineMix?.[pfId] ?? 0) / baselineFlexibleSum;
        pfVolume[pfId] += baselineFraction * flexibleRemaining;
      });
    }

    // Step 4: Normalize to sum 1
    const total = candidateProductFamilyIds.reduce((sum, pfIdRaw) => sum + Number(pfVolume[String(pfIdRaw)] ?? 0), 0);
    let normalized: Record<string, number> = { ...pfVolume };
    if (total > 0) {
      normalized = candidateProductFamilyIds.reduce<Record<string, number>>((acc, pfIdRaw) => {
        const pfId = String(pfIdRaw);
        const vol = Number(pfVolume[pfId] ?? 0);
        acc[pfId] = Math.round((vol / total) * 1e6) / 1e6;
        return acc;
      }, {});
    }

    return { distributionByProductFamilyId: normalized, totalAllocatedPercent: Math.round(assignedTotal * 1000) / 10 };
  };

  // Round each share to a whole percent and absorb the residual on the largest share, mirroring the
  // backend generator; independent per-item rounding of shares summing to 1.0 otherwise shows a phantom 101%.
  const roundDistributionToWholePercents = (
    dist: Record<string, number>
  ): Record<string, number> => {
    const entries = Object.entries(dist).map(
      ([k, v]) => [k, Math.round((Number(v) || 0) * 100) / 100] as [string, number]
    );
    if (entries.length === 0) return {};
    const sum = entries.reduce((s, [, v]) => s + v, 0);
    const residual = Math.round((1 - sum) * 100) / 100;
    if (residual !== 0) {
      let maxIdx = 0;
      for (let i = 1; i < entries.length; i++) {
        if (entries[i][1] > entries[maxIdx][1]) maxIdx = i;
      }
      entries[maxIdx][1] = Math.round((entries[maxIdx][1] + residual) * 100) / 100;
    }
    return entries.reduce<Record<string, number>>((acc, [k, v]) => {
      acc[k] = v;
      return acc;
    }, {});
  };

  const buildFullDrugPayerMatrixForAlternateScenario = (
    productFamilyIds: string[],
    payerList: PayerMixItem[],
    baseMatrix: Record<string, Record<string, boolean>>,
    payerAssignments: Record<string, string>,
    excludedPayersNormalized: Set<string> = new Set()
  ): Record<string, Record<string, boolean>> => {
    // Normalize assignment keys to tolerate casing/spacing mismatches
    const assignmentByNormalizedPayer: Record<string, string> = {};
    Object.entries(payerAssignments || {}).forEach(([payer, pfId]) => {
      assignmentByNormalizedPayer[normalizePayerName(payer)] = String(pfId);
    });

    const matrix: Record<string, Record<string, boolean>> = {};
    productFamilyIds.forEach((drugId) => {
      const baseRow = baseMatrix?.[drugId] ?? {};
      const baseRowByNormalizedPayer: Record<string, boolean> = {};
      Object.entries(baseRow || {}).forEach(([payerKey, allowed]) => {
        baseRowByNormalizedPayer[normalizePayerName(String(payerKey))] = (allowed as any) === true;
      });
      const row: Record<string, boolean> = {};

      payerList.forEach((p) => {
        const payer = String(p.payer);
        const normalized = normalizePayerName(payer);
        // Payers fully deselected at generation stay excluded across ALL product
        // families — including any introduced in the alternate — so their volume
        // never re-enters the category.
        if (excludedPayersNormalized.has(normalized)) {
          row[payer] = false;
          return;
        }
        const assignedPfId = assignmentByNormalizedPayer[normalized];
        if (assignedPfId) {
          row[payer] = assignedPfId === String(drugId);
        } else {
          // Default to base matrix value, falling back to eligible (true)
          row[payer] = (baseRow[payer] ?? baseRowByNormalizedPayer[normalized] ?? true) === true;
        }
      });

      matrix[String(drugId)] = row;
    });

    return matrix;
  };

  const computeDrugMaxSharesLikeGenerator = (
    matrix: Record<string, Record<string, boolean>>,
    payerList: PayerMixItem[],
    productFamilyIds: string[]
  ): Record<string, number> => {
    return productFamilyIds.reduce<Record<string, number>>((acc, drugId) => {
      const row = matrix?.[String(drugId)] ?? {};
      const uncheckedPercent = payerList.reduce((sum, item) => {
        const isChecked = row[item.payer] ?? true;
        return !isChecked ? sum + Number(item.percent ?? 0) : sum;
      }, 0);
      acc[String(drugId)] = Math.round(Math.max(0, 100 - uncheckedPercent) * 10) / 10;
      return acc;
    }, {});
  };

  const payerMixByName = useMemo(() => {
    const map: Record<string, number> = {};
    for (const item of payerMix) {
      if (!item?.payer) continue;
      map[normalizePayerName(item.payer)] = Number(item.percent ?? 0);
    }
    return map;
  }, [payerMix]);

  const purposeBodyTemplate = (rowData: GroupedScenarioResult) => {
    const kind = rowData.scenarioKind?.trim();
    if (!kind) return <span className="text-500">—</span>;
    return <Tag value={kind} severity="info" />;
  };

  const payerAssignmentsToRows = (
    payerAssignments: Record<string, string> | undefined,
    productFamilyNameById: Record<string, string>,
    payerMixLookupByName: Record<string, number>
  ) => {
    if (!payerAssignments) {
      return [] as Array<{ payer: string; payerPercent?: number; productFamilyId: string; productFamilyName: string }>;
    }

    return Object.entries(payerAssignments)
      .map(([payer, assignedId]) => {
        const productFamilyId = String(assignedId);
        const productFamilyName = productFamilyNameById[productFamilyId] ?? productFamilyId;
        const payerPercent = payerMixLookupByName[normalizePayerName(payer)];
        return { payer, payerPercent, productFamilyId, productFamilyName };
      })
      .sort((a, b) => {
        const ap = typeof a.payerPercent === 'number' && !Number.isNaN(a.payerPercent) ? a.payerPercent : undefined;
        const bp = typeof b.payerPercent === 'number' && !Number.isNaN(b.payerPercent) ? b.payerPercent : undefined;

        if (ap === undefined && bp !== undefined) return 1;
        if (ap !== undefined && bp === undefined) return -1;
        if (ap !== undefined && bp !== undefined && bp !== ap) return bp - ap;

        return a.payer.localeCompare(b.payer);
      });
  };

  const formatPfLabel = (name: string) => name ? name.charAt(0).toUpperCase() + name.slice(1) : '—';

  const openDecisionLogicModal = (scenario: GroupedScenarioResult, opts?: { startAlternate?: boolean }) => {
    setDecisionLogicScenario(scenario);
    setDecisionLogicModalVisible(true);

    // Initialize alternate scenario state for this modal open
    setAlternateScenarioId(scenario.scenario_id);
    setAlternateModeEnabled(opts?.startAlternate === true);
    setAlternatePayerAssignments({ ...(scenario.payerAssignments ?? {}) });
  };

  const closeDecisionLogicModal = () => setDecisionLogicModalVisible(false);

  // Runs once the dialog has finished animating out. Everything here feeds what the dialog
  // renders, so clearing it on the click itself emptied the dialog before it closed.
  const resetDecisionLogicModal = () => {
    setDecisionLogicScenario(null);
    setAlternateScenarioId(null);
    setAlternateModeEnabled(false);
    setAlternatePayerAssignments({});
    setIsCalculatingAlternate(false);
    setAlternatePreviewExpanded(false);
  };

  // Legacy alternate scenario modal (distribution sliders)
  const openLegacyAlternateModal = (scenario: GroupedScenarioResult) => {
    setLegacyAlternateScenario(scenario);
    // Initialize sliders with the scenario's current distribution values
    const initial: Record<string, number> = {};
    scenario.details.forEach((d) => {
      initial[String(d.product_family_id)] = Number(d.future_distribution ?? 0);
    });
    setLegacyAlternateDistributions(initial);
    setLegacyAlternateModalVisible(true);
  };

  const closeLegacyAlternateModal = () => {
    setLegacyAlternateModalVisible(false);
    setLegacyAlternateScenario(null);
    setLegacyAlternateDistributions({});
    setIsCalculatingLegacyAlternate(false);
  };

  const legacyAlternateDistributionTotal = useMemo(() => {
    return Object.values(legacyAlternateDistributions).reduce((sum, v) => sum + (Number(v) || 0), 0);
  }, [legacyAlternateDistributions]);

  const hasLegacyDistributionChanges = useMemo(() => {
    if (!legacyAlternateScenario) return false;
    const original: Record<string, number> = {};
    legacyAlternateScenario.details.forEach((d) => {
      original[String(d.product_family_id)] = Number(d.future_distribution ?? 0);
    });
    return Object.keys(legacyAlternateDistributions).some((pfId) => {
      const orig = Math.round((original[pfId] ?? 0) * 10000);
      const alt = Math.round((legacyAlternateDistributions[pfId] ?? 0) * 10000);
      return orig !== alt;
    });
  }, [legacyAlternateScenario, legacyAlternateDistributions]);

  // Build the admin-fee map (product_family_id -> fee per EU) for an alternate
  // scenario recalculation. Mirrors the generator payload so fees aren't dropped.
  // Source priority: cached generator fees (carried from the generate page),
  // falling back to the SP-returned admin_fee_per_eu (AdminFeePerEU column) on
  // the scenario result rows.
  const buildAlternateProductFamilyFeesMap = (
    productFamilyIds: string[],
    details: ScenarioResult[]
  ): Record<string, number> => {
    const cachedFees = state.generatorState?.form?.product_family_fees_map ?? {};
    const feesFromResults: Record<string, number> = {};
    details.forEach((d) => {
      const fee = d.admin_fee_per_eu;
      if (fee != null && !Number.isNaN(Number(fee))) {
        feesFromResults[String(d.product_family_id)] = Number(fee);
      }
    });
    const map: Record<string, number> = {};
    productFamilyIds.forEach((id) => {
      const key = String(id);
      const fee = cachedFees[key] ?? feesFromResults[key];
      if (fee != null && !Number.isNaN(Number(fee))) {
        map[key] = Math.round(Number(fee) * 100) / 100;
      }
    });
    return map;
  };

  // Same carry-forward as buildAlternateProductFamilyFeesMap, for the direct rebate: cached
  // generator input first, then the SP-returned direct_rebate_per_eu on the result rows (which
  // is what an alternate recalculated from a favorite or a reloaded session has).
  const buildAlternateProductFamilyDirectRebatesMap = (
    productFamilyIds: string[],
    details: ScenarioResult[]
  ): Record<string, number> => {
    const cached = state.generatorState?.form?.product_family_direct_rebates_map ?? {};
    const fromResults: Record<string, number> = {};
    details.forEach((d) => {
      const rebate = d.direct_rebate_per_eu;
      if (rebate != null && !Number.isNaN(Number(rebate))) {
        fromResults[String(d.product_family_id)] = Number(rebate);
      }
    });
    const map: Record<string, number> = {};
    productFamilyIds.forEach((id) => {
      const key = String(id);
      const rebate = cached[key] ?? fromResults[key];
      if (rebate != null && !Number.isNaN(Number(rebate))) {
        map[key] = Math.max(0, Math.round(Number(rebate) * 100) / 100);
      }
    });
    return map;
  };

  const runLegacyAlternateCalculation = async () => {
    if (!state.currentUser || !legacyAlternateScenario) return;

    const details = legacyAlternateScenario.details;
    const category = selectedCategory || details[0]?.category;
    if (!category) return;

    // Validate total is exactly 100%
    const total = legacyAlternateDistributionTotal;
    const totalPctCheck = Math.round(total * 100);
    if (totalPctCheck !== 100) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Invalid distribution',
        detail: `Distributions must sum to exactly 100%. Current total: ${totalPctCheck}%`,
        life: 3500,
      });
      return;
    }

    setIsCalculatingLegacyAlternate(true);
    try {
      // Normalize distributions to sum exactly to 1
      const rawTotal = Object.values(legacyAlternateDistributions).reduce((s, v) => s + (Number(v) || 0), 0);
      const normalizedMap: Record<string, number> = {};
      Object.entries(legacyAlternateDistributions).forEach(([pfId, val]) => {
        normalizedMap[pfId] = rawTotal > 0 ? (Number(val) || 0) / rawTotal : 0;
      });

      // Build excludedScenarios from cached state
      const cachedExcludedOpportunities = state.generatorState?.form?.selectedExcludedOpportunities ?? [];
      const excludedScenarios = cachedExcludedOpportunities
        .filter(opp => opp.category === category)
        .map(opp => ({
          logged_in_user: state.currentUser || '',
          category: opp.category || category,
          product_family_id: Number(opp.product_family_id),
          paid_on: opp.paid_on || '',
          rebate: Number(opp.rebate) ?? 0,
        }));

      const feesMap = buildAlternateProductFamilyFeesMap(Object.keys(normalizedMap), details);
      const directRebatesMap = buildAlternateProductFamilyDirectRebatesMap(Object.keys(normalizedMap), details);

      const payload: any = {
        logged_in_user: state.currentUser,
        category,
        max_scenarios: 1,
        min_distribution_map: normalizedMap,
        product_family_fees_map: Object.keys(feesMap).length > 0 ? feesMap : undefined,
        product_family_direct_rebates_map: Object.keys(directRebatesMap).length > 0 ? directRebatesMap : undefined,
        excludedScenarios,
        // Keep alternate scenarios at the same category size the user modeled on generation.
        category_scale_factor: state.generatorState?.form?.category_scale_factor_map?.[category] ?? 1.0,
        batch_or_alternate: 'alternate',
      };

      const resp = await ApiService.generateAndCalculateScenariosWithMinsAndMaxAndPvt(payload);

      const rawResults = (resp as any)?.results ?? [];
      const rawSummary = (resp as any)?.summary ?? [];

      // Remap scenario IDs to avoid collisions (same logic as payer-based alternate)
      const existingScenarioIds = Array.from(
        new Set(
          (selectedCategory
            ? state.scenarioResults.filter(r => r.category === category)
            : state.scenarioResults
          ).map(r => Number(r.scenario_id))
        )
      );
      const maxExisting = existingScenarioIds.length ? Math.max(...existingScenarioIds) : 0;
      const responseScenarioIds = Array.from(new Set((rawResults as any[]).map(r => Number(r.scenario_id))));
      const scenarioIdMap: Record<number, number> = {};
      let nextId = Math.max(maxExisting, 0) + 1;

      responseScenarioIds.forEach((sid) => {
        if (sid === 0) {
          scenarioIdMap[sid] = 0;
          return;
        }
        while (existingScenarioIds.includes(nextId) || pinnedAlternateScenarioIds.includes(nextId)) nextId += 1;
        scenarioIdMap[sid] = nextId;
        nextId += 1;
      });

      const results = (rawResults as any[]).map((r) => ({
        ...r,
        scenario_id: scenarioIdMap[Number(r.scenario_id)] ?? Number(r.scenario_id),
      }));

      const summaryArray = Array.isArray(rawSummary) ? rawSummary : [rawSummary];
      let summary = (summaryArray as any[]).map((s) => ({
        ...s,
        scenario_id: scenarioIdMap[Number(s.scenario_id)] ?? Number(s.scenario_id),
      }));

      // Pin created scenario ids
      const createdPinned = Object.entries(scenarioIdMap)
        .map(([k, v]) => ({ from: Number(k), to: Number(v) }))
        .filter(({ from, to }) => from !== 0 && to !== 0)
        .map(({ to }) => to);

      if (createdPinned.length > 0) {
        const byScenarioId = results.reduce((acc, r) => {
          const sid = Number((r as any).scenario_id);
          if (!acc[sid]) acc[sid] = [];
          acc[sid].push(r);
          return acc;
        }, {} as Record<number, any[]>);

        createdPinned.forEach((sid) => {
          const existing = summary.find((s) => Number((s as any).scenario_id) === sid);
          if (!existing) {
            const rows: ScenarioResult[] = (byScenarioId[sid] ?? []) as ScenarioResult[];
            const totalScenarioNcr = rows.reduce((sum: number, r: ScenarioResult) => sum + Number(r.total_estimated_ncr || 0), 0);
            const uniquePf = new Set(rows.map((r: ScenarioResult) => String(r.product_family_id)));
            summary = [
              {
                scenario_id: sid,
                logged_in_user: state.currentUser,
                total_scenario_ncr: totalScenarioNcr,
                product_count: uniquePf.size,
                created_at: new Date().toISOString(),
              } as any,
              ...summary,
            ];
          }
        });

        setPinnedAlternateScenarioIds((prev) => {
          const next = [...prev, ...createdPinned.filter(id => !prev.includes(id))];
          return next;
        });

        // Track which parent scenario each alternate was derived from
        const parentMap: Record<number, number> = {};
        createdPinned.forEach((sid) => {
          parentMap[sid] = legacyAlternateScenario.scenario_id;
        });
        dispatch(scenarioActions.mergeAlternateParentMap(parentMap));
      }

      if (Array.isArray(results) && results.length > 0) {
        dispatch(scenarioActions.appendScenarioResults(results as any, summary as any));
        // Merge rebate drill-down rows for the alternate, remapping scenario ids the same way.
        // Without this the renumbered scenario has no rows under its new id, so the product
        // family label renders unlinked and the export's rebate sheet skips it.
        const respRebateDetails = (resp as any)?.rebate_details as RebateDetail[] | undefined | null;
        if (respRebateDetails && respRebateDetails.length > 0) {
          dispatch(scenarioActions.mergeRebateDetails(
            respRebateDetails.map(d => ({
              ...d,
              scenario_id: scenarioIdMap[Number(d.scenario_id)] ?? Number(d.scenario_id),
            }))
          ));
        }
        toast.current?.show({
          severity: 'success',
          summary: 'Calculated',
          detail: 'Alternate scenario calculated and added to results.',
          life: 3000,
        });
        closeLegacyAlternateModal();
      } else {
        toast.current?.show({
          severity: 'warn',
          summary: 'No results',
          detail: 'The calculate endpoint returned no results.',
          life: 3500,
        });
      }
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Calculate failed',
        detail: handleApiError(e),
        life: 4500,
      });
    } finally {
      setIsCalculatingLegacyAlternate(false);
    }
  };

  const renderLegacyAlternateContent = () => {
    if (!legacyAlternateScenario) return null;

    const details = legacyAlternateScenario.details;
    const categoryKey = selectedCategory || details?.[0]?.category;
    const allFamiliesForCategory = categoryKey ? (productFamiliesByCategory[categoryKey] ?? []) : [];

    // Build rows: prefer category-level product families, fallback to scenario details
    type LegacySliderRow = { productFamilyId: string; productFamilyName: string; currentDistribution: number };
    let sliderRows: LegacySliderRow[];

    if (allFamiliesForCategory.length > 0) {
      sliderRows = allFamiliesForCategory
        .filter((pf) => pf.product_family_id !== null && pf.product_family_id !== undefined)
        .map((pf) => {
          const pfId = String(pf.product_family_id);
          const detail = details.find((d) => String(d.product_family_id) === pfId);
          return {
            productFamilyId: pfId,
            productFamilyName: String(pf.product_family_name ?? ''),
            currentDistribution: Number(detail?.future_distribution ?? 0),
          };
        });
    } else {
      sliderRows = details.map((d) => ({
        productFamilyId: String(d.product_family_id),
        productFamilyName: String(d.product_family_name ?? ''),
        currentDistribution: Number(d.future_distribution ?? 0),
      }));
    }

    sliderRows.sort((a, b) => formatPfLabel(a.productFamilyName).localeCompare(formatPfLabel(b.productFamilyName)));

    // Compute max market share per product family from payer preferences (same formula as Generator)
    // maxShare = 100% minus the sum of payer percentages for unchecked (ineligible) payers
    const maxShareByPfId: Record<string, number> = {};
    if (payerMix.length > 0) {
      sliderRows.forEach((row) => {
        const pfId = row.productFamilyId;
        const eligibilityRow = drugPayerMatrix[pfId] || {};
        const uncheckedPercent = payerMix.reduce((sum, item) => {
          const isChecked = eligibilityRow[item.payer] ?? true; // default eligible if not set
          return !isChecked ? sum + item.percent : sum;
        }, 0);
        maxShareByPfId[pfId] = Math.round(Math.max(0, 100 - uncheckedPercent) * 10) / 10;
      });
    }
    const hasPayerData = payerMix.length > 0 && Object.keys(drugPayerMatrix).length > 0;

    const total = legacyAlternateDistributionTotal;
    const totalPct = Math.round(total * 100);
    const isValid = totalPct === 100;

    const renderMiniFillBar = (fraction: number, fillColor: string = '#173A63') => {
      const safe = Number.isFinite(fraction) ? Math.max(0, Math.min(1, fraction)) : 0;
      return (
        <div className="flex align-items-center gap-2">
          <div
            className="distribution-bar"
            style={{
              width: '60px',
              height: '8px',
              backgroundColor: '#e5e7eb',
              borderRadius: '4px',
              overflow: 'hidden',
              flex: '0 0 auto',
            }}
          >
            <div
              className="scenario-results-mini-bar-fill"
              style={{
                width: `${safe * 100}%`,
                height: '100%',
                backgroundColor: fillColor,
                borderRadius: '4px',
              }}
            />
          </div>
          <span className="text-sm font-medium">{formatPercentage(safe)}</span>
        </div>
      );
    };

    // Build preview rows for Current vs Alternate table (same structure as Decision Logic modal)
    const previewRows = sliderRows.map((row) => ({
      productFamilyId: row.productFamilyId,
      productFamilyName: row.productFamilyName,
      currentDistribution: row.currentDistribution,
      alternateDistribution: legacyAlternateDistributions[row.productFamilyId] ?? 0,
    }));

    return (
      <div className="mb-3">
        {/* Sticky header — mirrors Decision Logic modal layout */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            background: 'var(--surface-a)',
            paddingBottom: '0.75rem',
            marginBottom: '0.75rem',
          }}
        >
          <div className="flex flex-column gap-2">
            <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
              <div className="text-sm text-700">
                <strong>{getScenarioDisplayLabel(legacyAlternateScenario.scenario_id)} vs Alternate %</strong>
              </div>
              <div className="flex align-items-center gap-2 flex-wrap">
                <div className="flex align-items-center gap-2">
                  <span className="text-sm font-semibold">Total:</span>
                  <Tag
                    value={`${totalPct}%`}
                    severity={isValid ? 'success' : 'danger'}
                  />
                  {!isValid && totalPct !== 100 && (
                    <span className="text-sm text-500">
                      ({totalPct < 100 ? `${100 - totalPct}% remaining` : `${totalPct - 100}% over`})
                    </span>
                  )}
                </div>
                <Button
                  label="Reset"
                  icon="pi pi-refresh"
                  className="p-button-sm p-button-text"
                  onClick={() => {
                    const initial: Record<string, number> = {};
                    legacyAlternateScenario.details.forEach((d) => {
                      initial[String(d.product_family_id)] = Number(d.future_distribution ?? 0);
                    });
                    setLegacyAlternateDistributions(initial);
                  }}
                  disabled={isCalculatingLegacyAlternate}
                />
                <Button
                  label={isCalculatingLegacyAlternate ? 'Calculating\u2026' : 'Calculate'}
                  icon="pi pi-calculator"
                  className="p-button-sm"
                  onClick={runLegacyAlternateCalculation}
                  disabled={isCalculatingLegacyAlternate || !hasLegacyDistributionChanges || !isValid}
                />
              </div>
            </div>

            {/* Current vs Alternate distribution preview table */}
            <DataTable value={previewRows} responsiveLayout="scroll" size="small" className="p-datatable-sm">
              <Column
                header="Product Family"
                body={(r) => {
                  const raw = String((r as any).productFamilyName || '');
                  const pfId = String((r as any).productFamilyId || '');
                  const label = formatPfLabel(raw);
                  const color = raw ? getColorForFamily(raw) : '#d1d5db';
                  const altPct = Math.round((legacyAlternateDistributions[pfId] ?? 0) * 100);
                  const maxPct = maxShareByPfId[pfId];
                  const exceedsMax = hasPayerData && maxPct !== undefined && altPct > maxPct;
                  return (
                    <div className="flex align-items-center gap-2">
                      <span style={{ width: 12, height: 12, backgroundColor: color, borderRadius: 3, border: '1px solid rgba(0,0,0,0.15)' }} />
                      <span>{label}</span>
                      {exceedsMax && (
                        <>
                          <Tooltip target={`.legacy-preview-warn-${pfId}`} position="right" />
                          <i
                            className={`pi pi-exclamation-triangle legacy-preview-warn-${pfId}`}
                            data-pr-tooltip={`Exceeds max eligible share of ${maxPct}% based on payer preferences`}
                            style={{ color: '#e2a03f', fontSize: '0.85rem', cursor: 'help' }}
                          />
                        </>
                      )}
                    </div>
                  );
                }}
                style={{ minWidth: '260px' }}
              />
              <Column
                header={getScenarioDisplayLabel(legacyAlternateScenario.scenario_id)}
                body={(r) => renderMiniFillBar(Number((r as any).currentDistribution ?? 0))}
                style={{ width: '12rem' }}
              />
              <Column
                header="Alternate"
                body={(r) => renderMiniFillBar(Number((r as any).alternateDistribution ?? 0))}
                style={{ width: '14rem' }}
              />
            </DataTable>
          </div>
        </div>

        {/* Distribution inputs */}
        <div className="mb-2" style={{ borderBottom: '1px solid var(--surface-border)', paddingBottom: '0.5rem' }}>
          <strong className="text-sm text-700">Distribution adjustments</strong>
        </div>
        <div className="flex flex-column gap-2" style={{ maxWidth: '700px' }}>
          {sliderRows.map((row) => {
            const pfId = row.productFamilyId;
            const value = legacyAlternateDistributions[pfId] ?? 0;
            const color = row.productFamilyName ? getColorForFamily(row.productFamilyName) : '#d1d5db';
            const altPct = Math.round(value * 100);
            const maxPct = maxShareByPfId[pfId];
            const exceedsMax = hasPayerData && maxPct !== undefined && altPct > maxPct;

            return (
              <div key={pfId} className="flex align-items-center gap-3" style={{ padding: '0.35rem 0' }}>
                <div className="flex align-items-center gap-2" style={{ width: '200px', flexShrink: 0 }}>
                  <span style={{ width: 10, height: 10, backgroundColor: color, borderRadius: 2, border: '1px solid rgba(0,0,0,0.12)', flexShrink: 0 }} />
                  <span className="text-sm" style={{ fontWeight: 500 }}>{formatPfLabel(row.productFamilyName)}</span>
                  {exceedsMax && (
                    <Tooltip target={`.legacy-warn-${pfId}`} position="right" />
                  )}
                  {exceedsMax && (
                    <i
                      className={`pi pi-exclamation-triangle legacy-warn-${pfId}`}
                      data-pr-tooltip={`Exceeds max eligible share of ${maxPct}% based on payer preferences`}
                      style={{ color: '#e2a03f', fontSize: '0.8rem', cursor: 'help', flexShrink: 0 }}
                    />
                  )}
                </div>
                <InputNumber
                  value={altPct}
                  onValueChange={(e) => {
                    const pct = typeof e.value === 'number' ? e.value : 0;
                    const clamped = Math.max(0, Math.min(100, Math.round(pct)));
                    const asFraction = Math.round(clamped) / 100;
                    setLegacyAlternateDistributions((prev) => ({ ...prev, [pfId]: asFraction }));
                  }}
                  min={0}
                  max={100}
                  step={1}
                  suffix="%"
                  useGrouping={false}
                  minFractionDigits={0}
                  maxFractionDigits={0}
                  inputStyle={{ width: '52px', textAlign: 'center', padding: '0.35rem 0.25rem' }}
                  style={{ width: '70px' }}
                  placeholder="0%"
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDecisionLogicContent = (rowData: GroupedScenarioResult) => {
    const details = rowData.details;

    const categoryKey = selectedCategory || details?.[0]?.category;
    const allFamiliesForCategory = categoryKey ? (productFamiliesByCategory[categoryKey] ?? []) : [];

    // Payers that were fully deselected at generation time (every originally-selected
    // product family unchecked). They stay excluded for every alternate of this
    // scenario — regardless of which product families the user later assigns — so
    // their dropped volume never re-enters the category.
    //
    // Two sources, unioned so this survives page reloads / opening from Favorites:
    //   1. state.excludedPayerInfo — populated during generation in this session.
    //   2. Derived from the current category drug-payer matrix over the ORIGINAL
    //      generation's product families — the same rule the backend applies, so it
    //      still works after session state has been cleared.
    //
    // The exclusion basis is the ORIGINAL (non-alternate) scenarios' product families
    // THAT ACTUALLY CARRY VOLUME, NOT this scenario's own details. Two reasons:
    //   - Alternates are generated over the full category family list, so their details
    //     carry a broader PF set; using rowData.details would widen the basis and make a
    //     payer merely allowed on a never-selected family look "not excluded" (this is
    //     why an alternate-of-an-alternate lost the exclusion).
    //   - Original scenarios still contain zero-distribution rows for families that were
    //     never selected (shown at 0%). A payer that's allowed on such a 0-volume family
    //     would fail the "deselected on every family" test and lose its exclusion. The
    //     backend only checks families with min > 0, so we mirror that by requiring a
    //     non-zero distribution here.
    const originalSelectedPfIds = (() => {
      const ids = new Set<string>();
      state.scenarioResults.forEach((r) => {
        if (categoryKey && r.category !== categoryKey) return;
        if (pinnedAlternateScenarioIds.includes(Number(r.scenario_id))) return; // skip alternates
        if (Number(r.future_distribution ?? 0) <= 0) return; // skip 0-volume / never-selected families
        const id = String(r.product_family_id ?? '');
        if (id && id !== 'undefined' && id !== 'null') ids.add(id);
      });
      return ids;
    })();

    const scenarioProductFamilyIds = (originalSelectedPfIds.size > 0
      ? Array.from(originalSelectedPfIds)
      : details.map((d) => String(d.product_family_id))
    ).filter((id) => id !== '' && id !== 'undefined' && id !== 'null');

    const sessionExcludedPayers = (categoryKey
      ? state.excludedPayerInfo[categoryKey]?.excludedPayers ?? []
      : []
    ).map((p) => normalizePayerName(String(p)));

    const derivedExcludedPayers = (payerMix || [])
      .filter((p) => p && p.payer)
      .filter((p) => {
        if (scenarioProductFamilyIds.length === 0) return false;
        const norm = normalizePayerName(String(p.payer));
        // Excluded only if EXPLICITLY deselected on every one of this scenario's
        // product families. Missing/unknown entries default to eligible (not excluded),
        // so a missing matrix can never produce a false exclusion.
        return scenarioProductFamilyIds.every(
          (pfId) => (drugPayerMatrixByNormalizedPayer[pfId]?.[norm] ?? true) === false
        );
      })
      .map((p) => normalizePayerName(String(p.payer)));

    const excludedPayerNormalizedSet = new Set([
      ...sessionExcludedPayers,
      ...derivedExcludedPayers,
    ]);

    // Build an ID -> Product Family Name lookup from the category list (preferred), with scenario details as fallback.
    const productFamilyNameById = allFamiliesForCategory
      .reduce((acc, pf) => {
        const id = pf.product_family_id;
        const name = pf.product_family_name;
        if (id !== null && id !== undefined && name) {
          acc[String(id)] = String(name);
        }
        return acc;
      }, {} as Record<string, string>);

    // For the payer eligibility breakdown, always show all PFs from the category (when available).
    // Fallback to PFs present in the scenario details if the category list hasn't loaded.
    const payerBreakdownProductFamilies: Array<{ productFamilyId: string; productFamilyName: string }> =
      allFamiliesForCategory.length > 0
        ? allFamiliesForCategory
            .filter((pf) => pf.product_family_id !== null && pf.product_family_id !== undefined)
            .map((pf) => ({
              productFamilyId: String(pf.product_family_id),
              productFamilyName: String(pf.product_family_name ?? ''),
            }))
        : details
            .reduce((acc, d) => {
              const id = String(d.product_family_id);
              if (!acc.some((x) => x.productFamilyId === id)) {
                acc.push({
                  productFamilyId: id,
                  productFamilyName: String(d.product_family_name || ''),
                });
              }
              return acc;
            }, [] as Array<{ productFamilyId: string; productFamilyName: string }>);

    details.forEach((row) => {
      const id = row.product_family_id;
      const name = row.product_family_name;
      if (id !== null && id !== undefined && name) {
        productFamilyNameById[String(id)] = String(name);
      }
    });

    const payerRows = payerAssignmentsToRows(rowData.payerAssignments, productFamilyNameById, payerMixByName);
    const totalAllocatedPercent = payerRows.reduce((sum, r) => {
      const pct = typeof r.payerPercent === 'number' && !Number.isNaN(r.payerPercent) ? r.payerPercent : 0;
      return sum + pct;
    }, 0);
    const hasAnyAllocatedPercent = payerRows.some((r) => typeof r.payerPercent === 'number' && !Number.isNaN(r.payerPercent));

    // Detect product families whose final distribution was lifted above the payer volume
    // actually assigned to them (a minimum top-up). The extra volume is drawn from families
    // above their minimums, so the payer assignments legitimately total less than the
    // results-table percentage. The requested minimum (if known) is resolved by a
    // normalized name match so the message can show the true target the user set.
    const pctFmt = (n: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(n);
    const selectedFamilies = state.generatorState?.selectedFamilies ?? {};
    const requestedMinPctByName: Record<string, number> = {};
    Object.keys(selectedFamilies).forEach((k) => {
      requestedMinPctByName[k.trim().toLowerCase()] = Number(selectedFamilies[k]?.minDistribution ?? 0) * 100;
    });
    const assignedPercentByPfId: Record<string, number> = {};
    payerRows.forEach((r) => {
      const pct = typeof r.payerPercent === 'number' && !Number.isNaN(r.payerPercent) ? r.payerPercent : 0;
      const id = String(r.productFamilyId);
      assignedPercentByPfId[id] = (assignedPercentByPfId[id] ?? 0) + pct;
    });
    const raisedToFloorFamilies = details.reduce((acc, d) => {
      const id = String(d.product_family_id);
      if (acc.some((x) => x.id === id)) return acc;
      const finalPct = Number(d.future_distribution ?? 0) * 100;
      const assignedPct = assignedPercentByPfId[id] ?? 0;
      if (finalPct - assignedPct < 0.1) return acc;
      const name = productFamilyNameById[id] ?? String(d.product_family_name ?? id);
      const requestedMinPct = requestedMinPctByName[name.trim().toLowerCase()] ?? 0;
      // Only surface the "minimum distribution applied" note when a minimum was
      // actually set for this family. Otherwise pure rounding/redistribution gaps
      // between the assigned payer volume (e.g. 44.7%) and the final distribution
      // (e.g. 45%) would trigger a misleading "lifted to meet its minimum" message
      // even when no minimum was configured.
      if (requestedMinPct <= 0) return acc;
      acc.push({ id, name, assignedPct, finalPct, requestedMinPct });
      return acc;
    }, [] as Array<{ id: string; name: string; assignedPct: number; finalPct: number; requestedMinPct: number }>);


    // Per-payer profitability for this scenario, keyed by normalized payer name so
    // lookups tolerate casing/spacing. Computed inline with the generation response.
    const profitabilityForScenario = profitabilityByScenarioId[rowData.scenario_id] ?? {};
    const profitabilityByNormalizedPayer: Record<string, PayerProfitabilityItem> = {};
    Object.entries(profitabilityForScenario).forEach(([payer, item]) => {
      profitabilityByNormalizedPayer[normalizePayerName(String(payer))] = item;
    });

    // Which basis each payer's figure rests on. Falls back to the legacy boolean when the
    // response comes from a backend that predates `basis`.
    const profitabilityBasis = (item: PayerProfitabilityItem) =>
      item.basis ?? (item.fee_schedule_missing ? 'medicare_estimate' : 'fee_schedule');

    // Product families in this scenario whose payers have no fee schedule at all, so their
    // profitability is suppressed. Drives the notice under the table.
    const familiesWithoutFeeSchedule = payerRows.reduce((acc, r) => {
      const item = profitabilityByNormalizedPayer[normalizePayerName(String(r.payer))];
      if (!item || profitabilityBasis(item) !== 'unavailable') return acc;
      const name = (r.productFamilyName && r.productFamilyName !== r.productFamilyId)
        ? formatPfLabel(String(r.productFamilyName))
        : '';
      if (name && !acc.includes(name)) acc.push(name);
      return acc;
    }, [] as string[]);

    const renderProfitabilityCell = (payer: string) => {
      const item = profitabilityByNormalizedPayer[normalizePayerName(payer)];
      if (!item) {
        return <span className="text-500">—</span>;
      }
      const warnClass = `profit-fee-warn-${normalizePayerName(payer).replace(/[^a-z0-9]/g, '')}`;

      // No payer on this product family has an entered rate, so every payer would be priced at
      // the same Medicare rate and the figure would just re-state the payer mix. Show that it
      // can't be calculated instead of a number that implies payer-specific precision.
      if (profitabilityBasis(item) === 'unavailable' || item.profit == null) {
        return (
          <div className="flex align-items-center gap-2">
            <span className="text-500">—</span>
            <Tooltip target={`.${warnClass}`} position="top" />
            <i
              className={`pi pi-info-circle ${warnClass}`}
              data-pr-tooltip="No fee schedule entered for this product family, so payer profitability can't be calculated. Add reimbursement rates to see it."
              style={{ color: '#6b7280', fontSize: '0.8rem', cursor: 'help' }}
            />
          </div>
        );
      }

      const isGreen = item.indicator === 'green';
      const isRed = item.indicator === 'red';
      // Subtle, text-only styling that matches the app's currency convention
      // (.currency-positive/.currency-negative/.currency-neutral) instead of a loud
      // filled Tag, so the numbers read cleanly at a glance.
      const toneClass = isGreen ? 'currency-positive' : isRed ? 'currency-negative' : 'currency-neutral';
      const icon = isGreen ? 'pi pi-arrow-up' : isRed ? 'pi pi-arrow-down' : 'pi pi-minus';
      const ariaLabel = `${isGreen ? 'Profitable' : isRed ? 'Unprofitable' : 'Break-even'}: ${formatCurrency(item.profit)}`;
      return (
        <div className="flex align-items-center gap-2" aria-label={ariaLabel}>
          <span className={`profit-value ${toneClass}`}>
            <i className={`${icon} profit-value-icon`} aria-hidden="true" />
            {formatCurrency(item.profit)}
          </span>
          {item.fee_schedule_missing && (
            <>
              <Tooltip target={`.${warnClass}`} position="top" />
              <i
                className={`pi pi-exclamation-triangle ${warnClass}`}
                data-pr-tooltip="No fee schedule rate entered for this payer — reimbursement uses the CMS Medicare rate, so this figure is an estimate."
                style={{ color: '#d97706', fontSize: '0.8rem', cursor: 'help' }}
              />
            </>
          )}
        </div>
      );
    };
    const expandedForScenario = expandedDecisionPayers[rowData.scenario_id] ?? {};

    const isAlternateForThisScenario = alternateModeEnabled && alternateScenarioId === rowData.scenario_id;

    const hasAlternateAssignmentChanges = (() => {
      if (!isAlternateForThisScenario) return false;

      const base = rowData.payerAssignments ?? {};
      const baseByNormalized: Record<string, string> = {};
      Object.entries(base).forEach(([payer, pfId]) => {
        baseByNormalized[normalizePayerName(String(payer))] = String(pfId);
      });

      const altByNormalized: Record<string, string> = {};
      Object.entries(alternatePayerAssignments ?? {}).forEach(([payer, pfId]) => {
        altByNormalized[normalizePayerName(String(payer))] = String(pfId);
      });

      const keys = Object.keys(baseByNormalized).concat(Object.keys(altByNormalized));
      const seen: Record<string, true> = {};
      for (let i = 0; i < keys.length; i += 1) {
        const k = keys[i];
        if (seen[k]) continue;
        seen[k] = true;
        if ((baseByNormalized[k] ?? '') !== (altByNormalized[k] ?? '')) return true;
      }
      return false;
    })();

    const alternatePayerRows = payerRows.map((r) => {
      const payerKey = String(r.payer);
      return {
        ...r,
        selectedProductFamilyId: String(alternatePayerAssignments[payerKey] ?? r.productFamilyId),
      };
    });

    const productFamilyOptionsAll = Object.entries(productFamilyNameById)
      .map(([id, name]) => ({ label: formatPfLabel(String(name)), value: String(id) }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const currentDistributionByPfId = details.reduce((acc, r) => {
      const id = String(r.product_family_id);
      acc[id] = Number(r.future_distribution ?? 0);
      return acc;
    }, {} as Record<string, number>);

    const candidateProductFamilyIds = Object.keys(productFamilyNameById)
      .map((id) => String(id))
      .filter((id) => id !== '');

    const payerItems = (payerMix || [])
      .filter((p) => p && p.payer)
      .filter((p) => !excludedPayerNormalizedSet.has(normalizePayerName(String(p.payer))))
      .map((p) => ({
        payer: String(p.payer),
        vol: Number(p.percent ?? 0) / 100,
      }));

    const { distributionByProductFamilyId: rawAlternateDistributionByPfId } =
      isAlternateForThisScenario && hasAlternateAssignmentChanges
        ? computeDistributionFromAssignments({
          assignments: alternatePayerAssignments,
          candidateProductFamilyIds,
          payerItems,
          baselineMix: currentDistributionByPfId,
          // No explicit mins available in the Results view; default to 0s.
          minDistributionMap: {},
        })
        : { distributionByProductFamilyId: { ...currentDistributionByPfId } };

    // Match the backend's whole-percent rounding so the preview totals 100% and equals the generated scenario.
    const alternateDistributionByPfId =
      isAlternateForThisScenario && hasAlternateAssignmentChanges
        ? roundDistributionToWholePercents(rawAlternateDistributionByPfId)
        : rawAlternateDistributionByPfId;

    const renderMiniFillBar = (fraction: number, fillColor: string = '#173A63') => {
      const safe = Number.isFinite(fraction) ? Math.max(0, Math.min(1, fraction)) : 0;
      return (
        <div className="flex align-items-center gap-2">
          <div
            className="distribution-bar"
            style={{
              width: '60px',
              height: '8px',
              backgroundColor: '#e5e7eb',
              borderRadius: '4px',
              overflow: 'hidden',
              flex: '0 0 auto',
            }}
          >
            <div
              className="scenario-results-mini-bar-fill"
              style={{
                width: `${safe * 100}%`,
                height: '100%',
                backgroundColor: fillColor,
                borderRadius: '4px',
              }}
            />
          </div>
          <span className="text-sm font-medium">{formatPercentage(safe)}</span>
        </div>
      );
    };

    const productFamilyPreviewRows = Object.entries(productFamilyNameById)
      .map(([pfId, pfName]) => {
        const current = Number(currentDistributionByPfId[String(pfId)] ?? 0);
        const alt = isAlternateForThisScenario
          ? Number(alternateDistributionByPfId[String(pfId)] ?? 0)
          : current;
        return {
          productFamilyId: String(pfId),
          productFamilyName: String(pfName),
          currentDistribution: current,
          alternateDistribution: alt,
        };
      })
      .sort((a, b) => formatPfLabel(a.productFamilyName).localeCompare(formatPfLabel(b.productFamilyName)));

    const runAlternateCalculation = async () => {
      if (!state.currentUser) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Missing user',
          detail: 'Please select a user before calculating.',
          life: 3000,
        });
        return;
      }

      if (!selectedCategory && !details?.[0]?.category) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Missing competitive therapeutic area',
          detail: 'Please select a competitive therapeutic area before calculating.',
          life: 3000,
        });
        return;
      }

      setIsCalculatingAlternate(true);
      try {
        const category = selectedCategory || details[0].category;
        const productFamilyIds = productFamilyPreviewRows.map((r) => String(r.productFamilyId));
        const fullMatrix = buildFullDrugPayerMatrixForAlternateScenario(
          productFamilyIds,
          payerMix,
          drugPayerMatrix,
          alternatePayerAssignments,
          excludedPayerNormalizedSet
        );

        // Set min_distribution_map to the alternate distribution to collapse to a single possible scenario.
        // Use the unrounded distribution: whole-percent rounding here would quantize each share by up to
        // ~0.5% and shift the SP's derived units, so an out-then-back-in round trip would not return to the
        // original units. The whole-percent version is for display only.
        const minDistMap = productFamilyPreviewRows.reduce<Record<string, number>>((acc, r) => {
          const rawShare = Number(rawAlternateDistributionByPfId[String(r.productFamilyId)] ?? r.alternateDistribution ?? 0) || 0;
          acc[String(r.productFamilyId)] = Math.max(0, Math.min(1, rawShare));
          return acc;
        }, {});

        // Compute max shares like Generator (0..100 scale)
        const computedMaxSharesMap = productFamilyIds.reduce<Record<string, number>>((acc, id) => {
          acc[String(id)] = 100;
          return acc;
        }, {});

        // Build excludedScenarios from cached selectedExcludedOpportunities for this category
        const cachedExcludedOpportunities = state.generatorState?.form?.selectedExcludedOpportunities ?? [];
        const excludedScenarios = cachedExcludedOpportunities
          .filter(opp => opp.category === category)
          .map(opp => ({
            logged_in_user: state.currentUser || '',
            category: opp.category || category,
            product_family_id: Number(opp.product_family_id),
            paid_on: opp.paid_on || '',
            rebate: Number(opp.rebate) ?? 0,
          }));

        const feesMap = buildAlternateProductFamilyFeesMap(productFamilyIds, details);
        const directRebatesMap = buildAlternateProductFamilyDirectRebatesMap(productFamilyIds, details);

        const payload: any = {
          logged_in_user: state.currentUser,
          category,
          max_scenarios: 1,
          // Remove drugs with max share 0 from min_distribution_map (Generator behavior)
          // min_distribution_map: Object.fromEntries(
          //   Object.entries(minDistMap).filter(([drugId]) => (computedMaxSharesMap[drugId] ?? 0) > 0)
          // ),
          min_distribution_map: minDistMap,
          product_family_fees_map: Object.keys(feesMap).length > 0 ? feesMap : undefined,
          product_family_direct_rebates_map: Object.keys(directRebatesMap).length > 0 ? directRebatesMap : undefined,
          drugPayerMatrix: fullMatrix,
          drugMaxShares: computedMaxSharesMap,
          excludedScenarios,
          // Keep alternate scenarios at the same category size the user modeled on generation.
          category_scale_factor: state.generatorState?.form?.category_scale_factor_map?.[category] ?? 1.0,
          batch_or_alternate: 'alternate',
          // Send the user-chosen assignments so the backend computes per-payer profitability
          // for the modeled alternate scenario (returned on resp.profitability).
          payer_assignments: alternatePayerAssignments,
        };

        const resp = await ApiService.generateAndCalculateScenariosWithMinsAndMaxAndPvt(payload);

        const rawResults = (resp as any)?.results ?? [];
        const rawSummary = (resp as any)?.summary ?? [];

        // Remap backend-returned scenario_id (commonly 1) to a new unique id if it would collide with existing.
        // Scenario 0 is treated as base/current and can replace existing scenario 0.
        const existingScenarioIds = Array.from(
          new Set(
            (selectedCategory
              ? state.scenarioResults.filter(r => r.category === category)
              : state.scenarioResults
            ).map(r => Number(r.scenario_id))
          )
        );
        const maxExisting = existingScenarioIds.length ? Math.max(...existingScenarioIds) : 0;

        const responseScenarioIds = Array.from(new Set((rawResults as any[]).map(r => Number(r.scenario_id))));
        const scenarioIdMap: Record<number, number> = {};
        let nextId = Math.max(maxExisting, 0) + 1;

        responseScenarioIds.forEach((sid) => {
          if (sid === 0) {
            scenarioIdMap[sid] = 0;
            return;
          }

          const wouldCollide = existingScenarioIds.includes(sid) || pinnedAlternateScenarioIds.includes(sid);
          if (wouldCollide) {
            while (existingScenarioIds.includes(nextId) || pinnedAlternateScenarioIds.includes(nextId)) nextId += 1;
            scenarioIdMap[sid] = nextId;
            nextId += 1;
          } else {
            // Even if it doesn't collide, we still remap 1 so it doesn't look like an original scenario.
            if (sid === 1) {
              while (existingScenarioIds.includes(nextId) || pinnedAlternateScenarioIds.includes(nextId)) nextId += 1;
              scenarioIdMap[sid] = nextId;
              nextId += 1;
            } else {
              scenarioIdMap[sid] = sid;
            }
          }
        });

        const results = (rawResults as any[]).map((r) => ({
          ...r,
          scenario_id: scenarioIdMap[Number(r.scenario_id)] ?? Number(r.scenario_id),
        }));

        const summaryArray = Array.isArray(rawSummary) ? rawSummary : [rawSummary];
        let summary = (summaryArray as any[]).map((s) => ({
          ...s,
          scenario_id: scenarioIdMap[Number(s.scenario_id)] ?? Number(s.scenario_id),
        }));

        // Pin any newly created scenario ids (excluding 0) right under 0
        const createdPinned = Object.entries(scenarioIdMap)
          .map(([k, v]) => ({ from: Number(k), to: Number(v) }))
          .filter(({ from, to }) => from !== 0 && to !== 0)
          .map(({ to }) => to);

        // Backend typically won't return payer_assignments; carry it over for the newly created scenario(s).
        if (createdPinned.length > 0) {
          const byScenarioId = results.reduce((acc, r) => {
            const sid = Number((r as any).scenario_id);
            if (!acc[sid]) acc[sid] = [];
            acc[sid].push(r);
            return acc;
          }, {} as Record<number, any[]>);

          createdPinned.forEach((sid) => {
            const existing = summary.find((s) => Number((s as any).scenario_id) === sid);
            if (existing) {
              // Don't overwrite if backend unexpectedly provided something.
              if (!(existing as any).payer_assignments) {
                (existing as any).payer_assignments = { ...alternatePayerAssignments };
              }
              return;
            }

            const rows: ScenarioResult[] = (byScenarioId[sid] ?? []) as ScenarioResult[];
            const totalScenarioNcr = rows.reduce((sum: number, r: ScenarioResult) => sum + Number(r.total_estimated_ncr || 0), 0);
            const uniquePf = new Set(rows.map((r: ScenarioResult) => String(r.product_family_id)));

            summary = [
              {
                scenario_id: sid,
                logged_in_user: state.currentUser,
                total_scenario_ncr: totalScenarioNcr,
                product_count: uniquePf.size,
                created_at: new Date().toISOString(),
                payer_assignments: { ...alternatePayerAssignments },
              } as any,
              ...summary,
            ];
          });
        }

        if (createdPinned.length > 0) {
          setPinnedAlternateScenarioIds((prev) => {
            const next = [...prev, ...createdPinned.filter(id => !prev.includes(id))];
            return next;
          });

          // Track which parent scenario each alternate was derived from
          const parentMap: Record<number, number> = {};
          createdPinned.forEach((sid) => {
            parentMap[sid] = rowData.scenario_id;
          });
          dispatch(scenarioActions.mergeAlternateParentMap(parentMap));
        }

        if (Array.isArray(results) && results.length > 0) {
          dispatch(scenarioActions.appendScenarioResults(results as any, summary as any));
          // Merge inline profitability, remapping the backend scenario_id to the id we assigned.
          const respProfitability = (resp as any)?.profitability as Record<string, Record<string, PayerProfitabilityItem>> | undefined | null;
          if (respProfitability) {
            const remapped: Record<number, Record<string, PayerProfitabilityItem>> = {};
            Object.entries(respProfitability).forEach(([sid, byPayer]) => {
              const mappedId = scenarioIdMap[Number(sid)] ?? Number(sid);
              remapped[mappedId] = byPayer;
            });
            dispatch(scenarioActions.mergePayerProfitability(remapped));
          }
          // Merge rebate drill-down rows for the alternate, remapping scenario ids the same way.
          const respRebateDetails = (resp as any)?.rebate_details as RebateDetail[] | undefined | null;
          if (respRebateDetails && respRebateDetails.length > 0) {
            dispatch(scenarioActions.mergeRebateDetails(
              respRebateDetails.map(d => ({
                ...d,
                scenario_id: scenarioIdMap[Number(d.scenario_id)] ?? Number(d.scenario_id),
              }))
            ));
          }
          toast.current?.show({
            severity: 'success',
            summary: 'Calculated',
            detail: 'Alternate scenario calculated and added to results.',
            life: 3000,
          });

          // Close the Decision Logic modal once results are returned/appended
          closeDecisionLogicModal();
        } else {
          toast.current?.show({
            severity: 'warn',
            summary: 'No results',
            detail: 'The calculate endpoint returned no results.',
            life: 3500,
          });
        }
      } catch (e) {
        toast.current?.show({
          severity: 'error',
          summary: 'Calculate failed',
          detail: handleApiError(e),
          life: 4500,
        });
      } finally {
        setIsCalculatingAlternate(false);
      }
    };

    const toggleExpandedPayer = (payer: string) => {
      setExpandedDecisionPayers((prev) => {
        const current = prev[rowData.scenario_id] ?? {};
        const isOpen = current[payer] === true;
        // Single-expand behavior: either none or only the clicked payer
        const nextForScenario = isOpen ? {} : { [payer]: true };
        return { ...prev, [rowData.scenario_id]: nextForScenario };
      });
    };

    const payerRowExpansionTemplate = (payerRow: any) => {
      if (payerPrefsLoading) return <div className="text-500 text-sm">Loading payer preferences…</div>;
      if (payerPrefsError) return <div className="text-500 text-sm">{payerPrefsError}</div>;

      const payer = String(payerRow.payer);

      if (payerBreakdownProductFamilies.length === 0) {
        return <div className="text-500 text-sm">No product families found for this scenario.</div>;
      }

      return (
        <div style={{ padding: '0.5rem 0.75rem' }}>
          <DataTable value={payerBreakdownProductFamilies} responsiveLayout="scroll" size="small" className="p-datatable-sm">
            <Column
              header="Product Family"
              body={(r) => {
                const rawName = String(r.productFamilyName || '');
                const label = rawName ? formatPfLabel(rawName) : '—';
                const color = rawName ? getColorForFamily(rawName) : '#d1d5db';
                return (
                  <div className="flex align-items-center gap-2">
                    <span style={{ width: 12, height: 12, backgroundColor: color, borderRadius: 3, border: '1px solid rgba(0,0,0,0.15)' }} />
                    <span>{label}</span>
                  </div>
                );
              }}
            />
            <Column
              header="Allowed"
              body={(r) => {
                const pfId = String(r.productFamilyId);
                const allowed = (drugPayerMatrixByNormalizedPayer?.[pfId]?.[normalizePayerName(payer)] ?? true) === true;
                return <Tag value={allowed ? 'Allowed' : 'Not allowed'} severity={allowed ? 'success' : 'danger'} />;
              }}
              style={{ width: '10rem' }}
            />
          </DataTable>
        </div>
      );
    };

    return (
      <div className="mb-3">
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            background: 'var(--surface-a)',
            paddingBottom: '0.75rem',
            marginBottom: '0.75rem',
          }}
        >
          <div className="flex flex-column gap-2">
            <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
              <div className="text-sm text-700">
                <span />
              </div>
              {payerMixAvailable && payerRows.length > 0 && productFamilyOptionsAll.length > 0 && (
                <div className="flex align-items-center gap-2 flex-wrap">
                  {!isAlternateForThisScenario ? (
                    <span />
                  ) : (
                    <>
                      <Button
                        label="Back to payer assignments"
                        icon="pi pi-times"
                        className="p-button-sm p-button-text alt-cancel-button"
                        onClick={() => {
                          setAlternateModeEnabled(false);
                          setAlternatePayerAssignments({ ...(rowData.payerAssignments ?? {}) });
                        }}
                        disabled={isCalculatingAlternate}
                      />
                      <Button
                        label={isCalculatingAlternate ? 'Calculating…' : 'Calculate'}
                        icon="pi pi-calculator"
                        className="p-button-sm"
                        onClick={runAlternateCalculation}
                        disabled={isCalculatingAlternate || !hasAlternateAssignmentChanges}
                      />
                    </>
                  )}
                </div>
              )}
            </div>

            {!isAlternateForThisScenario && payerMixAvailable && payerRows.length > 0 && productFamilyOptionsAll.length > 0 && (
              <div className="flex justify-content-start">
                <Button
                  label="Run alternate scenario"
                  icon="pi pi-play"
                  className="p-button-sm p-button-outlined"
                  onClick={() => {
                    setAlternateScenarioId(rowData.scenario_id);
                    setAlternatePayerAssignments({ ...(rowData.payerAssignments ?? {}) });
                    setAlternateModeEnabled(true);
                  }}
                />
              </div>
            )}

            {isAlternateForThisScenario && (
              <>
              <div
                className="flex align-items-center gap-2 cursor-pointer"
                style={{ userSelect: 'none', padding: '0.25rem 0' }}
                onClick={() => setAlternatePreviewExpanded((prev) => !prev)}
              >
                <i className={`pi ${alternatePreviewExpanded ? 'pi-chevron-down' : 'pi-chevron-right'}`} style={{ fontSize: '0.85rem' }} />
                <span className="text-sm font-semibold">{getScenarioDisplayLabel(rowData.scenario_id)} vs Alternate %</span>
              </div>
              {alternatePreviewExpanded && (
              <DataTable value={productFamilyPreviewRows} responsiveLayout="scroll" size="small" className="p-datatable-sm">
                <Column
                  header="Product Family"
                  body={(r) => {
                    const raw = String((r as any).productFamilyName || '');
                    const label = formatPfLabel(raw);
                    const color = raw ? getColorForFamily(raw) : '#d1d5db';
                    return (
                      <div className="flex align-items-center gap-2">
                        <span style={{ width: 12, height: 12, backgroundColor: color, borderRadius: 3, border: '1px solid rgba(0,0,0,0.15)' }} />
                        <span>{label}</span>
                      </div>
                    );
                  }}
                  style={{ minWidth: '260px' }}
                />
                <Column
                  header={getScenarioDisplayLabel(rowData.scenario_id)}
                  body={(r) => renderMiniFillBar(Number((r as any).currentDistribution ?? 0))}
                  style={{ width: '12rem' }}
                />
                <Column
                  header="Alternate"
                  body={(r) => renderMiniFillBar(Number((r as any).alternateDistribution ?? 0))}
                  style={{ width: '14rem' }}
                />
              </DataTable>
              )}
              </>
            )}
          </div>
        </div>

        <div className="mb-2 flex align-items-center justify-content-between">
          <strong>Payer assignments:</strong>
          {payerRows.length > 0 && (() => {
            const sid = rowData?.scenario_id;
            const scenarioLabel = sid != null ? getScenarioDisplayLabel(Number(sid)) : `Scenario ${sid}`;

            const buildPayerAssignmentRows = () =>
              payerRows.map((pr) => ({
                scenario_id: Number(sid),
                payer: pr.payer,
                product_family_name: pr.productFamilyName ?? '—',
                ...(typeof pr.payerPercent === 'number' ? { payer_percent: pr.payerPercent } : {}),
              }));

            const doExport = (format: 'csv' | 'excel') => {
              toast.current?.show({ severity: 'info', summary: 'Export', detail: `Exporting payer assignments for ${scenarioLabel}`, life: 2000 });
              const scenarioResultRows = rowData.details ?? [];
              void handleExportWithPayerAssignments(
                format,
                scenarioResultRows,
                buildPayerAssignmentRows(),
                rebateDetailsForExport(scenarioResultRows),
                categoryIsOral
              );
            };

            const modalExportItems = [
              { label: 'Export as CSV', icon: 'pi pi-download', command: () => doExport('csv') },
              { label: 'Export as Excel', icon: 'pi pi-file-excel', command: () => doExport('excel') },
            ];

            return (
              <>
                <Menu model={modalExportItems} popup ref={modalExportMenuRef} />
                <Button
                  icon="pi pi-angle-down"
                  label="Export"
                  className="p-button-sm p-button-text"
                  onClick={(e) => modalExportMenuRef.current?.toggle(e)}
                />
              </>
            );
          })()}
        </div>
        {payerRows.length === 0 ? (
          <span className="text-500">—</span>
        ) : !isAlternateForThisScenario ? (
          <>
            <DataTable
              value={payerRows}
              responsiveLayout="scroll"
              size="small"
              className="p-datatable-sm"
              dataKey="payer"
              expandedRows={expandedForScenario}
              rowExpansionTemplate={payerRowExpansionTemplate}
              onRowClick={(e) => {
                const payer = (e.data as any)?.payer;
                if (!payer) return;
                toggleExpandedPayer(String(payer));
              }}
            >
              <Column
                field="payer"
                header="Payer (% of total)"
                style={{ minWidth: '260px' }}
                footer={() => {
                  const percentLabel = hasAnyAllocatedPercent
                    ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(totalAllocatedPercent) + '%'
                    : '—';
                  return (
                    <span>
                      Total % allocated{' '}
                      <span className="text-500">({percentLabel})</span>
                    </span>
                  );
                }}
                body={(r) => {
                  const isExpanded = expandedForScenario[String(r.payer)] === true;
                  const raw = typeof r.payerPercent === 'number' && !Number.isNaN(r.payerPercent)
                    ? r.payerPercent
                    : undefined;
                  const percentLabel = raw === undefined ? '' : ` (${raw}%)`;
                  return (
                    <span>
                      <i className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'}`} style={{ marginRight: 8, color: '#6b7280' }} />
                      {r.payer}
                      {percentLabel && <span className="text-500">{percentLabel}</span>}
                    </span>
                  );
                }}
              />
              <Column
                header="Product Family"
                footer={() => <span />}
                body={(r) => (
                  (() => {
                    const raw = (r.productFamilyName && r.productFamilyName !== r.productFamilyId)
                      ? String(r.productFamilyName)
                      : '';
                    if (!raw) return <span>—</span>;

                    const label = formatPfLabel(raw);
                    const color = getColorForFamily(raw);

                    return (
                      <div className="flex align-items-center gap-2">
                        <span style={{ width: 12, height: 12, backgroundColor: color, borderRadius: 3, border: '1px solid rgba(0,0,0,0.15)' }} />
                        <span>{label}</span>
                      </div>
                    );
                  })()
                )}
              />
              <Column
                header={() => (
                  <span className="inline-flex align-items-center gap-1">
                    Profitability
                    <Tooltip target=".profitability-info-icon" position="top" />
                    <i
                      className="pi pi-info-circle profitability-info-icon"
                      data-pr-tooltip={"Per-payer estimate from this payer's own fee-schedule rate: (reimbursement − price) × units + rebate share. Payers with no entered rate use the CMS Medicare rate and are marked as estimates. Shown as — when the product family has no fee schedule at all. Won't necessarily match the Weighted NCR."}
                      style={{ color: '#6c757d', fontSize: '0.85rem', cursor: 'pointer' }}
                    />
                  </span>
                )}
                footer={() => <span />}
                body={(r) => renderProfitabilityCell(String(r.payer))}
                style={{ minWidth: '180px' }}
              />
            </DataTable>
            {familiesWithoutFeeSchedule.length > 0 && (
              <div
                className="mt-3 p-3 border-round"
                style={{ background: '#fffbeb', border: '1px solid #fde68a' }}
              >
                <div className="flex align-items-start gap-2">
                  <i className="pi pi-exclamation-triangle" style={{ color: '#d97706', marginTop: 2, fontSize: '0.95rem' }} />
                  <div className="text-sm" style={{ color: '#78350f', lineHeight: 1.5 }}>
                    <span style={{ fontWeight: 600 }}>Payer profitability unavailable.</span>{' '}
                    No fee schedule has been entered for {familiesWithoutFeeSchedule.join(', ')}, so
                    every payer on {familiesWithoutFeeSchedule.length > 1 ? 'those families' : 'that family'} would
                    be priced at the same Medicare rate — the result would only re-state the payer
                    mix, not tell you which payer is profitable. Enter reimbursement rates to see it.
                  </div>
                </div>
              </div>
            )}
            {raisedToFloorFamilies.length > 0 && (
              <div
                className="mt-3 p-3 border-round"
                style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}
              >
                <div className="flex align-items-start gap-2">
                  <i className="pi pi-info-circle" style={{ color: '#2563eb', marginTop: 2, fontSize: '0.95rem' }} />
                  <div className="text-sm" style={{ color: '#1e3a5f', lineHeight: 1.5 }}>
                    <span style={{ fontWeight: 600 }}>Minimum distribution applied.</span>{' '}
                    {raisedToFloorFamilies.map((f, i) => {
                      const punct = i < raisedToFloorFamilies.length - 1 ? ';' : '.';
                      return (
                        <span key={f.id}>
                          {i > 0 ? ' ' : ''}
                          {f.requestedMinPct > 0 ? (
                            <>
                              {formatPfLabel(f.name)} was held to its {pctFmt(f.requestedMinPct)}% minimum
                            </>
                          ) : (
                            <>
                              {formatPfLabel(f.name)} was lifted to {pctFmt(f.finalPct)}% to meet its minimum
                              {` (assigned payers total ${pctFmt(f.assignedPct)}%)`}
                            </>
                          )}
                          {punct}
                        </span>
                      );
                    })}{' '}
                    The extra volume is drawn from families above their minimums, so the payer row totals
                    above might not match the distribution shown in the results table.
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <DataTable
              value={alternatePayerRows}
              responsiveLayout="scroll"
              size="small"
              className="p-datatable-sm"
              dataKey="payer"
            >
              <Column
                header="Payer (% of total)"
                body={(r) => {
                  const raw = typeof r.payerPercent === 'number' && !Number.isNaN(r.payerPercent)
                    ? r.payerPercent
                    : undefined;
                  const percentLabel = raw === undefined ? '' : ` (${raw}%)`;
                  return (
                    <span>
                      {r.payer}
                      {percentLabel && <span className="text-500">{percentLabel}</span>}
                    </span>
                  );
                }}
                style={{ minWidth: '260px' }}
              />
              <Column
                header="Assign to Product Family"
                body={(r) => {
                  const payer = String((r as any).payer);
                  const currentValue = String((r as any).selectedProductFamilyId ?? '');

                  // Filter options to only those allowed for this payer; if matrix is missing, default allow.
                  const payerOptions = productFamilyOptionsAll.filter((opt) => {
                    const allowed = (drugPayerMatrix?.[String(opt.value)]?.[payer] ?? true) === true;
                    return allowed;
                  });

                  // If there are 0 or 1 eligible options, don't show a changeable dropdown.
                  if (payerOptions.length === 0) {
                    return <span className="text-500">—</span>;
                  }
                  if (payerOptions.length === 1) {
                    return <span>{payerOptions[0].label}</span>;
                  }

                  return (
                    <Dropdown
                      value={currentValue ? currentValue : null}
                      options={payerOptions}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select"
                      onChange={(e) => {
                        const next = String(e.value);
                        setAlternatePayerAssignments((prev) => ({ ...prev, [payer]: next }));
                      }}
                      style={{ width: '100%', minWidth: 260 }}
                      disabled={isCalculatingAlternate || productFamiliesLoading}
                    />
                  );
                }}
              />
            </DataTable>

            {productFamiliesError && (
              <div className="text-500 text-sm mt-2">{productFamiliesError}</div>
            )}
          </>
        )}
      </div>
    );
  };


  // Column body templates
  const scenarioBodyTemplate = (rowData: GroupedScenarioResult) => {
    const label = getScenarioDisplayLabel(Number(rowData.scenario_id));
    const parentId = rowData.parentScenarioId;
    const parentLabel = parentId != null ? getScenarioDisplayLabel(parentId) : null;
    return (
      <div className="flex flex-column">
        <span className="font-semibold">{label}</span>
        {parentLabel && (
          <span className="text-xs text-500" style={{ lineHeight: 1.2 }}>from {parentLabel}</span>
        )}
      </div>
    );
  };

  const currencyBodyTemplate = (field: string) => (rowData: ScenarioResult) => {
    const value = (rowData as any)[field];
    return (
      <span className={value && value > 0 ? 'text-green-600 font-semibold' : 'text-gray-500'}>
        {formatCurrency(value)}
      </span>
    );
  };

  const totalPriceTemplate = (rowData: ScenarioResult) => {
    // const totalPrice = (rowData.net_price ?? 0) * (rowData.invoice_units ?? 0);
    const netPrice = rowData.net_price ?? 0;
    return (
      <span className={(netPrice) > 0 ? 'text-green-600 font-semibold' : 'text-gray-500'}>
        {formatCurrency(netPrice)}
      </span>
    );
  };


  const numberBodyTemplate = (field: string) => (rowData: ScenarioResult) => {
    const value = (rowData as any)[field];
    return formatNumber(value);
  };

  const distributionBodyTemplate = (rowData: ScenarioResult) => {
    return (
      <div className="flex align-items-center gap-2">
        <div
          className="distribution-bar"
          style={{
            width: '60px',
            height: '8px',
            backgroundColor: '#e5e7eb',
            borderRadius: '4px',
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              width: `${rowData.future_distribution * 100}%`,
              height: '100%',
              backgroundColor: '#173A63',
              borderRadius: '4px'
            }}
          />
        </div>
        <span className="text-sm font-medium">
          {formatPercentage(rowData.future_distribution)}
        </span>
      </div>
    );
  };

  const renderRowExpansion = (rowData: GroupedScenarioResult, opts?: { hideActions?: boolean }) => {
    const details = rowData.details;
    const hasPayerAssignments = !!rowData.payerAssignments && Object.keys(rowData.payerAssignments).length > 0;

    // Build the set of PF IDs missing a fee schedule for this row's category (used in weighted mode only)
    const category = selectedCategory || details[0]?.category;
    const missingPfIdSet = new Set<string>(
      (state.missingFeeSchedulePfIds?.[category ?? ''] ?? []).map(String)
    );
    // Per-PF partial gaps: PF has a schedule but is missing rates for some eligible payers.
    const feeGaps = state.feeScheduleGaps?.[category ?? ''] ?? {};

    return (
      <div className="expansion-fit-viewport" style={{ padding: '0 1rem 1rem 1rem' }}>
        <div className="flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <h5 className="m-0">Detailed Breakdown</h5>

          {!opts?.hideActions && (
          <div className="flex align-items-center gap-2 flex-wrap">
            <Button
              label="View payer assignments"
              icon="pi pi-eye"
              className="p-button-sm p-button-text"
              onClick={() => openDecisionLogicModal(rowData)}
              disabled={!hasPayerAssignments}
            />
            {hasPayerAssignments ? (
              <Button
                label="Run alternate scenario"
                icon="pi pi-play"
                className="p-button-sm p-button-outlined"
                onClick={() => openDecisionLogicModal(rowData, { startAlternate: true })}
                disabled={!payerMixAvailable}
              />
            ) : (
              <Button
                label="Run alternate scenario"
                icon="pi pi-play"
                className="p-button-sm p-button-outlined"
                onClick={() => openLegacyAlternateModal(rowData)}
              />
            )}
          </div>
          )}
        </div>
        <DataTable className="detailed-breakdown-table" value={details} responsiveLayout="scroll" size="small">
          <Column
            field="product_family_name"
            header="Product Family"
            body={(rowData: ScenarioResult) => {
              const label = rowData.product_family_name
                ? rowData.product_family_name.charAt(0).toUpperCase() + rowData.product_family_name.slice(1)
                : '';
              const isMissing = ncrMode === 'weighted' && missingPfIdSet.has(String(rowData.product_family_id));
              const tooltipId = `fee-sched-tip-${rowData.product_family_id}`;
              // Partial gap only when the PF isn't wholly missing (whole-PF warning takes priority).
              const gap = (ncrMode === 'weighted' && !isMissing)
                ? feeGaps[String(rowData.product_family_id)]
                : undefined;
              const gapTooltipId = `fee-gap-tip-${rowData.product_family_id}`;
              const gapTooltip = gap
                ? `Missing fee-schedule rate${gap.payers.length > 1 ? 's' : ''} for ${gap.payers
                    .map(p => `${p.payer} (${p.pct.toFixed(1)}%)`)
                    .join(', ')} — ~${gap.fallback_pct.toFixed(1)}% of this product family's volume uses Medicare, so weighted NCR is approximate.`
                : '';
              const openRebateDetail = () => {
                setRebateDetailTarget({
                  scenarioId: Number(rowData.scenario_id),
                  pfId: String(rowData.product_family_id),
                  pfLabel: label,
                });
                setRebateDetailVisible(true);
              };
              // Only linked when there are rows, so the dialog cannot open onto nothing.
              // "No contracts" vs "not loaded" are different claims — the latter is not ours
              // to make when we simply have no rows yet.
              const isEarning = pfRebateEarning.get(
                `${Number(rowData.scenario_id)}:${String(rowData.product_family_id)}`
              );
              // A direct rebate is a column on this row rather than a rebate row, so it can be
              // the only thing the drill-down has to show -- the link has to open for it too.
              const hasDirectRebate = Number(rowData.total_direct_rebate ?? 0) > 0;
              const hasRebates = isEarning !== undefined || hasDirectRebate;
              const rebateTip = hasRebates
                ? (isEarning || hasDirectRebate
                    ? 'View projected rebate performance'
                    : 'View potential rebate performance')
                : rebateDetails.length > 0
                  ? 'No rebate contracts exist for this product family'
                  : "Rebate detail isn't loaded for this session — run a new generation to view it";
              // PrimeReact Tooltip, not a native `title`, so it picks up .p-tooltip styling.
              const rebateTipId = `rebate-tip-${rowData.scenario_id}-${rowData.product_family_id}`;
              return (
                <span className="flex align-items-center gap-1">
                  {hasRebates ? (
                    <span
                      id={rebateTipId}
                      role="button"
                      tabIndex={0}
                      data-pr-tooltip={rebateTip}
                      data-pr-position="top"
                      onClick={openRebateDetail}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openRebateDetail(); } }}
                      style={{ color: 'var(--primary-color)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                    >
                      {label}
                    </span>
                  ) : (
                    <span
                      id={rebateTipId}
                      style={{ cursor: 'default' }}
                      data-pr-tooltip={rebateTip}
                      data-pr-position="top"
                    >
                      {label}
                    </span>
                  )}
                  <Tooltip target={`#${rebateTipId}`} />
                  {isMissing && (
                    <>
                      <i
                        id={tooltipId}
                        className="pi pi-exclamation-triangle"
                        style={{ color: '#d97706', fontSize: '0.8rem', cursor: 'default' }}
                        data-pr-tooltip="No fee schedule found — weighted NCR is using Medicare rates for this product family."
                        data-pr-position="top"
                      />
                      <Tooltip target={`#${tooltipId}`} />
                    </>
                  )}
                  {gap && (
                    <>
                      <i
                        id={gapTooltipId}
                        className="pi pi-exclamation-circle"
                        style={{ color: '#d97706', fontSize: '0.8rem', cursor: 'default' }}
                        data-pr-tooltip={gapTooltip}
                        data-pr-position="top"
                      />
                      <Tooltip target={`#${gapTooltipId}`} />
                    </>
                  )}
                </span>
              );
            }}
          />
          {/* <Column field="category" header="Category" body={(rowData) => rowData.category} /> */}
          <Column field="future_distribution" header="Distribution" body={distributionBodyTemplate} />
          {/* <Column field="contract_units" header="Contract Units" body={numberBodyTemplate('contract_units')} /> */}
          {/* <Column field="invoice_units" header="Invoice Units" body={numberBodyTemplate('invoice_units')} /> */}
          <Column field={unitsField} header={unitsHeader} body={numberBodyTemplate(unitsField)} />
          {ncrMode === 'medicare' ? (
            <Column field="estimated_medicare_reimbursement" header={`${ncrLabel} Reimb.`} body={currencyBodyTemplate('estimated_medicare_reimbursement')} />
          ) : (
            <Column field="estimated_pvt_reimbursement" header="Weighted Reimb." body={currencyBodyTemplate('estimated_pvt_reimbursement')} />
          )}
          {ncrMode === 'medicare' ? (
            <Column field={`estimated_medicare_reimbursement${perUnitSuffix}`} header={`${ncrLabel} Reimb. ${perUnitLabel}`} body={currencyBodyTemplate(`estimated_medicare_reimbursement${perUnitSuffix}`)} />
          ) : (
            <Column field={`estimated_pvt_reimbursement${perUnitSuffix}`} header={`Weighted Reimb. ${perUnitLabel}`} body={currencyBodyTemplate(`estimated_pvt_reimbursement${perUnitSuffix}`)} />
          )}
          {/* No Direct Rebate column here on purpose: it is already inside the Rebate column
              (added once per product family), so a column of its own beside that one would read
              as a second figure to add. The rebate drill-down is where it is broken out. */}
          <Column
            field="total_admin_fee"
            header="Admin Fee"
            body={(rowData: ScenarioResult) => {
              const val = rowData.total_admin_fee ?? 0;
              return <span className={val > 0 ? 'text-green-600 font-semibold' : 'text-gray-500'}>{formatCurrency(val)}</span>;
            }}
          />
          <Column field="price" header="Price" body={totalPriceTemplate} />
          <Column field="total_rebate" header="Rebate" body={currencyBodyTemplate('total_rebate')} />
          {ncrMode === 'medicare' ? (
            <Column field="total_estimated_ncr" header={`${ncrLabel} NCR`} body={currencyBodyTemplate('total_estimated_ncr')} />
          ) : (
            <Column field="total_weighted_ncr" header="Weighted NCR" body={currencyBodyTemplate('total_weighted_ncr')} />
          )}
          {ncrMode === 'medicare' ? (
            <Column field={`total_estimated_ncr${perUnitSuffix}`} header={`${ncrLabel} NCR ${perUnitLabel}`} body={currencyBodyTemplate(`total_estimated_ncr${perUnitSuffix}`)} />
          ) : (
            <Column field={`total_weighted_ncr${perUnitSuffix}`} header={`Weighted NCR ${perUnitLabel}`} body={currencyBodyTemplate(`total_weighted_ncr${perUnitSuffix}`)} />
          )}
        </DataTable>
      </div>
    );
  };

  const getFilteredResults = (): GroupedScenarioResult[] => {
    const filtered = orderedScenarioResults;

    const grouped = filtered.reduce((acc, row) => {
      if (!acc[row.scenario_id]) acc[row.scenario_id] = [];
      acc[row.scenario_id].push(row);
      return acc;
    }, {} as Record<number, ScenarioResult[]>);

    const list = Object.entries(grouped).map(([scenarioId, rows]) => {
      const sid = Number(scenarioId);
      // Find the summary matching this scenario and the selected category (to get correct payer_assignments).
      // Fall back to any summary for this scenario_id if no category is selected or no category-specific match.
      const categorySummary = selectedCategory
        ? state.scenarioSummary.find(s => s.scenario_id === sid && s.category === selectedCategory)
        : undefined;
      const summary = categorySummary ?? state.scenarioSummary.find(s => s.scenario_id === sid);
      const totalNCR = rows.reduce((sum, r) => sum + Number(r.total_estimated_ncr || 0), 0);
      const totalWeightedNCR = rows.reduce((sum, r) => sum + Number(r.total_weighted_ncr || 0), 0);
      const totalRebate = rows.reduce((sum, r) => sum + Number(r.total_rebate || 0), 0);
      const totalMedicareReimb = rows.reduce((sum, r) => sum + Number(r.estimated_medicare_reimbursement || 0), 0);
      const totalPvtReimb = rows.reduce((sum, r) => sum + Number(r.estimated_pvt_reimbursement || 0), 0);
      const totalPrice = rows.reduce((sum, r) => sum + (Number(r.net_price || 0)), 0);

      // const sid = Number(scenarioId);
      const parentId = state.alternateParentMap?.[sid];
      return {
        scenario_id: sid,
        summary,
        scenarioKind: (summary as any)?.scenario_kind,
        payerAssignments: (summary as any)?.payer_assignments,
        parentScenarioId: parentId,
        totalNCR,
        totalWeightedNCR,
        totalRebate,
        totalMedicareReimb,
        totalPvtReimb,
        totalPrice,
        distributionBreakdown: getDistributionBreakdown(rows),
        details: rows,
      };
    });

    return [...list].sort((a, b) => sortScenarioIdsWithPinnedAlternates(a.scenario_id, b.scenario_id));
  };

  // Compute best NCR respecting category selection and ncrMode
  const computeBestNCR = () => {
    const field = ncrMode === 'weighted' ? 'total_weighted_ncr' : 'total_estimated_ncr';
    if (selectedCategory) {
      const filtered = state.scenarioResults.filter(r => r.category === selectedCategory);
      const byScenario = filtered.reduce((acc, r) => {
        acc[r.scenario_id] = (acc[r.scenario_id] || 0) + Number(r[field] || 0);
        return acc;
      }, {} as Record<number, number>);
      const values = Object.values(byScenario);
      return values.length ? Math.max(...values) : 0;
    }
    const summaryValues = state.scenarioSummary.map(s => s.total_scenario_ncr).filter(v => typeof v === 'number');
    return summaryValues.length ? Math.max(...summaryValues) : 0;
  };

  // Toolbar content
  // Toolbar left side (now only ranking toggle since filters are page-level)
  const leftToolbarTemplate = () => (
    <div className="flex align-items-start gap-3 w-full flex-column">
      <div
        ref={categoryContainerRef}
        className="category-segmented"
      >
        {categoryOptions.map(opt => (
          <button
            key={opt.value}
            type="button"
            className={`category-segmented-btn${selectedCategory === opt.value ? ' active' : ''}`}
            onClick={() => setSelectedCategory(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {isToolbarStacked && (
        <div className="flex align-items-center gap-2 mt-2 flex-wrap" style={{ width: '100%' }}>
          {rightToolbarInner()}
        </div>
      )}
    </div>
  );


  // Inner right toolbar content so it can be reused stacked or inline
  const rightToolbarInner = () => (
    <>
      {payerMixAvailable && (
        <div className="ncr-toggle">
          <button
            type="button"
            className={`ncr-toggle-btn${ncrMode === 'medicare' ? ' active' : ''}`}
            onClick={() => setNcrMode('medicare')}
          >
            {ncrLabel} NCR
          </button>
          <button
            type="button"
            id="weighted-ncr-toggle"
            className={`ncr-toggle-btn${ncrMode === 'weighted' ? ' active' : ''}${(allFeeSchedulesMissing || categoryIsOral) ? ' disabled' : ''}`}
            aria-disabled={allFeeSchedulesMissing || categoryIsOral}
            onClick={() => { if (!allFeeSchedulesMissing && !categoryIsOral) setNcrMode('weighted'); }}
            {...((allFeeSchedulesMissing || categoryIsOral) && {
              'data-pr-tooltip': categoryIsOral
                ? 'Weighted NCR is not applicable for oral inventory categories.'
                : 'Weighted NCR disabled \u2014 no fee schedules for this category.',
              'data-pr-position': 'top',
            })}
          >
            Weighted NCR
          </button>
          {(allFeeSchedulesMissing || categoryIsOral) && <Tooltip target="#weighted-ncr-toggle" />}
        </div>
      )}
      {payerMixLoaded && (
        <div className="ncr-toggle">
          <button
            type="button"
            className={`ncr-toggle-btn${unitMode === 'category' ? ' active' : ''}`}
            onClick={() => setUnitMode('category')}
          >
            Category Units
          </button>
          <button
            type="button"
            className={`ncr-toggle-btn${unitMode === 'invoice' ? ' active' : ''}`}
            onClick={() => setUnitMode('invoice')}
          >
            Invoice Units
          </button>
        </div>
      )}
      <Button
        icon="pi pi-clone"
        label={`Compare (${selectedForComparison.length})`}
        disabled={selectedForComparison.length < 2}
        className="p-button-outlined p-button-sm"
        onClick={() => setComparisonModalVisible(true)}
      />
      {(() => {
        const productFamilyNameByIdForExport: Record<string, string> = {};
        for (const row of selectedCategoryResults) {
          const pfId = String((row as any)?.product_family_id ?? '');
          const pfName = String((row as any)?.product_family_name ?? '');
          if (!pfId || !pfName) continue;
          if (!productFamilyNameByIdForExport[pfId]) {
            productFamilyNameByIdForExport[pfId] = pfName;
          }
        }

        const payerAssignmentsByScenarioId: Record<string, Record<string, string> | undefined> = {};
        for (const s of state.scenarioSummary) {
          payerAssignmentsByScenarioId[String((s as any)?.scenario_id)] = (s as any)?.payer_assignments;
        }

        const mapPayerAssignmentsToNames = (assignments: Record<string, string> | undefined) => {
          if (!assignments) return undefined;
          const out: Record<string, string> = {};
          for (const [payer, pfIdRaw] of Object.entries(assignments)) {
            const pfId = String(pfIdRaw);
            out[payer] = productFamilyNameByIdForExport[pfId] ?? pfId;
          }
          return out;
        };

        const attachPayerAssignments = (rows: ScenarioResult[]) => {
          return rows.map((r) => {
            const payer_assignments = mapPayerAssignmentsToNames(
              payerAssignmentsByScenarioId[String((r as any)?.scenario_id)]
            );
            return {
              ...(r as any),
              payer_assignments,
            } as ScenarioResult;
          });
        };

        const getExportPayload = () => {
          if (selectedForComparison.length === 0) {
            return {
              data: attachPayerAssignments(selectedCategoryResults),
              message: 'Exporting all scenarios',
            };
          }

          const selectedScenarioCount = selectedForComparison.length;
          const rowsFromSelection: ScenarioResult[] = [];
          for (const group of selectedForComparison) {
            if (group.details && group.details.length > 0) {
              rowsFromSelection.push(...group.details);
            }
          }

          if (rowsFromSelection.length > 0) {
            return {
              data: attachPayerAssignments(rowsFromSelection),
              message: `Exporting ${selectedScenarioCount} scenario${selectedScenarioCount === 1 ? '' : 's'}`,
            };
          }

          const selectedIdMap: Record<string, true> = {};
          for (const group of selectedForComparison) {
            selectedIdMap[String(group.scenario_id)] = true;
          }
          const filtered: ScenarioResult[] = [];
          for (const row of selectedCategoryResults) {
            if (selectedIdMap[String(row.scenario_id)]) {
              filtered.push(row);
            }
          }

          return {
            data: attachPayerAssignments(filtered),
            message: `Exporting ${selectedScenarioCount} scenario${selectedScenarioCount === 1 ? '' : 's'}`,
          };
        };

        const buildPayerAssignmentRows = (rows: ScenarioResult[]) => {
          const seen = new Set<number>();
          const result: Array<{ scenario_id: number; payer: string; product_family_name: string; payer_percent?: number }> = [];
          for (const r of rows) {
            const sid = Number((r as any)?.scenario_id);
            if (seen.has(sid)) continue;
            seen.add(sid);
            const assignments = payerAssignmentsByScenarioId[String(sid)];
            if (!assignments) continue;
            for (const [payer, pfIdRaw] of Object.entries(assignments)) {
              const pfId = String(pfIdRaw);
              const pfName = productFamilyNameByIdForExport[pfId] ?? pfId;
              const pct = payerMixByName[normalizePayerName(payer)];
              result.push({
                scenario_id: sid,
                payer,
                product_family_name: pfName,
                ...(typeof pct === 'number' ? { payer_percent: pct } : {}),
              });
            }
          }
          return result;
        };

        const getExportScenarioCount = () => {
          if (selectedForComparison.length > 0) return selectedForComparison.length;
          const seen: Record<string, true> = {};
          let count = 0;
          for (const row of selectedCategoryResults) {
            const id = String(row.scenario_id);
            if (seen[id]) continue;
            seen[id] = true;
            count += 1;
          }
          return count;
        };

        const exportScenarioCount = getExportScenarioCount();

        const exportItems = [
          {
            label: 'Export as CSV',
            icon: 'pi pi-download',
            command: () => {
              const { data, message } = getExportPayload();
              const payerRows = buildPayerAssignmentRows(data);
              const rebateRows = rebateDetailsForExport(data);
              toast.current?.show({ severity: 'info', summary: 'Export', detail: message, life: 2000 });
              if (payerRows.length > 0) {
                void handleExportWithPayerAssignments('csv', data, payerRows, rebateRows, categoryIsOral);
              } else {
                void handleExport('csv', data, rebateRows, categoryIsOral);
              }
            },
          },
          {
            label: 'Export as Excel',
            icon: 'pi pi-file-excel',
            command: () => {
              const { data, message } = getExportPayload();
              const payerRows = buildPayerAssignmentRows(data);
              const rebateRows = rebateDetailsForExport(data);
              toast.current?.show({ severity: 'info', summary: 'Export', detail: message, life: 2000 });
              if (payerRows.length > 0) {
                void handleExportWithPayerAssignments('excel', data, payerRows, rebateRows, categoryIsOral);
              } else {
                void handleExport('excel', data, rebateRows, categoryIsOral);
              }
            },
          },
        ];
        return (
          <>
            <Menu model={exportItems} popup ref={exportMenuRef} />
            <Button
              label={`Export (${exportScenarioCount})`}
              icon="pi pi-angle-down"
              className="p-button-outlined p-button-sm"
              onClick={(e) => exportMenuRef.current?.toggle(e)}
              disabled={isExporting || selectedCategoryResults.length === 0}
            />
          </>
        );
      })()}
    </>
  );

  const rightToolbarTemplate = () => {
    if (isToolbarStacked) return null; // hide when stacked; rendered inside left template
    return <div className="flex align-items-center gap-2">{rightToolbarInner()}</div>;
  };

  if (isVerifyingAccess) {
    return (
      <div className="scenario-results-container flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <ProgressSpinner style={{ color: '#42A5F5' }}/>
      </div>
    );
  }

  if (state.scenarioResults.length === 0) {
    return (
      <div className="scenario-results-container">
        <Card>
          <div className="text-center p-6">
            <i className="pi pi-chart-bar text-6xl text-300 mb-4"></i>
            <h3 className="text-900 mb-3">No Scenario Results Available</h3>
            <p className="text-600 mb-4">
              Generate scenarios first to view results and analysis.
            </p>

            <Button
              label="Generate Scenarios"
              icon="pi pi-plus"
              className="generate-button"
              onClick={() => navigate('/generate')}
            />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="scenario-results-container">
      <Toast ref={toast} />



      {/* Summary Cards */}
      <div className="grid mb-4">
        <div className="col-12 md:col-6 lg:col-6">
          <Card className="dashboard-card">
            <div className="flex justify-content-between align-items-center">
              <div>
                <div className="text-500 font-medium mb-2">Organization Name</div>
                <div className="text-900 font-medium text-xl">
                  {state.currentOrg ? `${state.currentOrg}` : 'Not Selected'}
                </div>
              </div>
              <div className="bg-blue-100 border-round p-3">
                <i className="pi pi-building text-blue-500 text-xl"></i>
              </div>

            </div>
          </Card>
        </div>

        <div className="col-12 md:col-6 lg:col-3">
          <Card className="dashboard-card">
            <div className="flex justify-content-between align-items-center">
              <div>
                <div className="text-500 font-medium mb-2">Competitive Therapeutic Area</div>
                <div className="text-900 font-medium text-xl">
                  {selectedCategory || 'All Competitive Therapeutic Areas'}
                </div>
              </div>
              <div className="bg-pink-100 border-round p-3">
                <i className="pi pi-tags text-pink-500 text-xl"></i>
              </div>
            </div>
          </Card>
        </div>

        <div className="col-12 md:col-6 lg:col-3">
          <Card className="dashboard-card">
            <div className="flex justify-content-between align-items-center">
              <div>
                <div className="text-500 font-medium mb-2">{ncrMode === 'medicare' ? `Best ${ncrLabel} NCR` : 'Best Weighted NCR'}</div>
                <div className="text-900 font-medium text-xl">
                  {formatCurrency(computeBestNCR())}
                </div>
              </div>
              <div className="bg-orange-100 border-round p-3">
                <i className="pi pi-chart-bar text-orange-500 text-xl"></i>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Scenario Analysis chart temporarily hidden */}
      {/* <div style={{ paddingBottom: 16 }}>
        <ScenarioChart
          data={aggregatedScenarioData as any}
          category={selectedCategory || undefined}
          ncrMode={ncrMode}
          getScenarioLabel={getScenarioDisplayLabel}
        />
      </div> */}

      <div style={{ paddingBottom: 16 }}>
        <QtdDistributionChart
          data={selectedCategory ? (productFamiliesByCategory[selectedCategory] ?? []) : []}
          category={selectedCategory || undefined}
          loading={productFamiliesLoading}
          colorForFamily={getColorForFamily}
        />
      </div>

      {/* Informational banners between chart and detailed results */}
      {state.generationStatus && state.generationStatus.inProgress && !generationBannerDismissed && (
        <div className="flex align-items-center justify-content-between mb-3 p-3 border-round"
          style={{ background: '#edf1f7', border: '1px solid #c2cfe0', fontSize: '1rem', color: '#0f2847' }}>
          <div className="flex align-items-center gap-2">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.1rem' }}></i>
            <span>
              <strong>Generating categories:</strong>{' '}
              {state.generationStatus.completedCategories} / {state.generationStatus.totalCategories} completed.
              Results auto-append as they finish.
            </span>
          </div>
          <Button
            icon="pi pi-times"
            className="p-button-text p-button-rounded p-button-sm"
            style={{ color: '#0f2847' }}
            onClick={() => setGenerationBannerDismissed(true)}
            aria-label="Dismiss"
          />
        </div>
      )}

      {(() => {
        const info = selectedCategory
          ? state.excludedPayerInfo[selectedCategory]
          : Object.values(state.excludedPayerInfo)[0];
        if (!info || excludedPayerBannerDismissed) return null;
        const pct = (info.excludedPayerVolume * 100).toFixed(1);
        return (
          <div className="flex align-items-center justify-content-between mb-3 p-3 border-round"
            style={{ background: '#fdf2f2', border: '1px solid #e5c2c2', fontSize: '1rem', color: '#7f1d1d' }}>
            <div className="flex align-items-center gap-2">
              <i className="pi pi-exclamation-circle" style={{ fontSize: '1.2rem' }}></i>
              <div>
                <strong>{pct}% of last quarter volume excluded from the generated scenarios (not the baseline or alternate scenarios you run)</strong>
                <div className="mt-1">
                  Payers with all product families deselected: <strong>{info.excludedPayers.join(', ')}</strong>
                </div>
              </div>
            </div>
            <Button
              icon="pi pi-times"
              className="p-button-text p-button-rounded p-button-sm"
              style={{ color: '#7f1d1d' }}
              onClick={() => setExcludedPayerBannerDismissed(true)}
              aria-label="Dismiss"
            />
          </div>
        );
      })()}

      {/* Category selector moved into toolbar above */}

      {/* <h1 className="text-xl font-bold mb-4">Scenario Analysis</h1>
    <ScenarioExpert results={getFilteredResults()}/> */}

      <Tooltip target=".pi-info-circle" />
      {/* Results Table */}
      <Card
        title={
          <span>
            Detailed Results{' '}
            <i
              className="pi pi-info-circle"
              data-pr-tooltip={ncrMode === 'medicare'
                ? `NCR: (${ncrLabel} Reimbursement + Rebate + Admin Fee) - Price\nPrice: Net Price * Invoice Units\nInvoice Units: Period to date volume, by Account by NDC, unadjusted for Opportunity measures of 'Units'`
                : `Weighted NCR: (Weighted Reimbursement + Rebate + Admin Fee) - Price\nPrice: Net Price * Invoice Units\nInvoice Units: Period to date volume, by Account by NDC, unadjusted for Opportunity measures of 'Units'`}
              data-pr-position="right"
              style={{
                cursor: 'pointer',
                marginLeft: '0.5rem',
                color: '#6c757d', // muted gray
                fontSize: '1rem'
              }}
            />
          </span>
        }
      >

        <div className="toolbar-card mb-3" style={{ backgroundColor: '#ffffff' }}>
          <Toolbar
            className="results-toolbar-inner"
            left={leftToolbarTemplate}
            right={rightToolbarTemplate}
          />
        </div>
        {/* Legend: only product families actually present (non-zero distribution) in scenarios for selected category */}
        <div className="flex flex-wrap gap-3 mb-3" style={{ marginLeft: 8 }}>
          {(() => {
            if (!selectedCategory) return null;
            // Gather rows for the selected category with a positive future_distribution
            const rows = state.scenarioResults.filter(r => r.category === selectedCategory && (r.future_distribution ?? 0) > 0);
            if (rows.length === 0) return <span className="text-sm text-500">No products for this competitive therapeutic area.</span>;
            const orderedFamilies: string[] = [];
            const seen = new Set<string>();
            rows.forEach(r => {
              const fam = r.product_family_name || 'Other';
              if (!seen.has(fam)) {
                seen.add(fam);
                orderedFamilies.push(fam);
              }
            });
            // Match the QTD chart: order by category-unit share, largest first.
            // Keyed case-insensitively since name casing can differ across data sources.
            const familyRank = new Map<string, number>();
            [...(productFamiliesByCategory[selectedCategory] ?? [])]
              .sort((a, b) => (b.product_family_to_category_qtd_ratio ?? 0) - (a.product_family_to_category_qtd_ratio ?? 0))
              .forEach((pf, i) => familyRank.set((pf.product_family_name || '').trim().toLowerCase(), i));
            const rankOf = (fam: string) => familyRank.has(fam.trim().toLowerCase())
              ? (familyRank.get(fam.trim().toLowerCase()) as number)
              : Number.MAX_SAFE_INTEGER;
            orderedFamilies.sort((a, b) => rankOf(a) - rankOf(b));
            return orderedFamilies.map(family => {
              const color = getColorForFamily(family);
              return (
                <div key={family} className="flex align-items-center gap-2" style={{ whiteSpace: 'nowrap' }}>
                  <div style={{ width: 16, height: 16, backgroundColor: color, borderRadius: 4, border: '1px solid #ccc' }} />
                  <span className="text-sm">{family.charAt(0).toUpperCase() + family.slice(1)}</span>
                </div>
              );
            });
          })()}
        </div>
        <DataTable
          // isDemoMode is in the key for the same reason as in FavoriteScenarios:
          // it resolves asynchronously and DataTable memoizes body templates.
          key={`datatable-${selectedCategory || 'all'}-${ncrMode}-${unitMode}-demo-${isDemoMode}`}
          value={getFilteredResults()
            .map(r => {
              const filteredDetails = selectedCategory ? r.details.filter(d => d.category === selectedCategory) : r.details;
              return {
                ...r,
                details: filteredDetails,
                totalNCR: filteredDetails.reduce((sum, d) => sum + Number(d.total_estimated_ncr || 0), 0),
                totalWeightedNCR: filteredDetails.reduce((sum, d) => sum + Number(d.total_weighted_ncr || 0), 0),
                totalPrice: filteredDetails.reduce((sum, d) => sum + (Number(d.net_price || 0)), 0),
                totalRebate: filteredDetails.reduce((sum, d) => sum + Number(d.total_rebate || 0), 0),
                totalMedicareReimb: filteredDetails.reduce((sum, d) => sum + Number(d.estimated_medicare_reimbursement || 0), 0),
                totalPvtReimb: filteredDetails.reduce((sum, d) => sum + Number(d.estimated_pvt_reimbursement || 0), 0),
                distributionBreakdown: getDistributionBreakdown(filteredDetails)
              };
            })
            .filter(r => r.details.length > 0)}
          selection={selectedForComparison}
          onSelectionChange={(e) => setSelectedForComparison(e.value)}
          selectionMode="multiple"
          dataKey="scenario_id"
          expandedRows={expandedRows}
          onRowToggle={(e) => {
            if (!Array.isArray(e.data)) setExpandedRows(e.data);
          }}
          rowClassName={(rowData) => selectedForComparison.some(s => s.scenario_id === rowData.scenario_id) ? 'p-highlight' : ''}
          rowExpansionTemplate={(rowData) => renderRowExpansion(rowData)}
          /* Show all scenarios without pagination */
          globalFilter={globalFilter}
          filters={filters}
          emptyMessage="No results found"
          className="p-datatable-sm expansion-fit-table"
          size="small"
        >
          <Column
            header={() => null}
            body={(rowData) => {
              const isSelected = selectedForComparison.some(s => s.scenario_id === rowData.scenario_id);
              const disableCheckbox = selectedForComparison.length >= 2 && !isSelected;
              // Check if this complete scenario is favorited
              const isFavorite = rowData.details.length > 0 ? favorites.has(getScenarioSignature(rowData.details)) : false;

              // See the note in FavoriteScenarios: per-row class, and the isDemoMode
              // segment in the DataTable key is what lets a late flag reach this cell.
              const demoTipClass = `favorite-star-demo-${rowData.scenario_id}`;
              return (
                <div className="flex align-items-center gap-2">
                  {isDemoMode && <Tooltip target={`.${demoTipClass}`} position="top" />}
                  <i
                    className={`pi ${isFavorite ? 'pi-star-fill text-yellow-500' : 'pi-star text-gray-400'} ${isDemoMode ? `opacity-50 ${demoTipClass}` : 'cursor-pointer'}`}
                    style={{ fontSize: '1.2rem' }}
                    data-pr-tooltip={isDemoMode ? 'Read-only demo account' : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isDemoMode) return;
                      toggleFavorite(rowData);
                    }}
                  />
                  <Checkbox
                    checked={isSelected}
                    disabled={disableCheckbox}
                    onChange={() => {
                      let newSelection;
                      if (isSelected) {
                        newSelection = selectedForComparison.filter(s => s.scenario_id !== rowData.scenario_id);
                      } else if (selectedForComparison.length < 2) {
                        newSelection = [...selectedForComparison, rowData];
                      } else {
                        newSelection = selectedForComparison;
                      }
                      setSelectedForComparison(newSelection);
                    }}
                  />
                </div>
              );
            }}
            style={{ width: '5rem', textAlign: 'center' }}
          />
          <Column expander style={{ width: '3em' }} />
          <Column
            field="scenario_id"
            header={() => (
              <span>
                Scenario{' '}
                <i
                  className="pi pi-info-circle"
                  data-pr-tooltip="Rankings are for scenario comparison only."
                  data-pr-position="top"
                  style={{ cursor: 'pointer', color: '#6c757d', fontSize: '0.85rem' }}
                />
              </span>
            )}
            body={scenarioBodyTemplate}
            sortable
            sortFunction={pinnedSort}
            style={{ minWidth: '80px' }}
          />
          {/* <Column
            field="scenarioKind"
            header="Purpose"
            body={purposeBodyTemplate}
            sortable
            style={{ minWidth: '140px' }}
          /> */}
          <Column
            header="Distribution"
            body={(rowData) => {
              const visibleDist = rowData.distributionBreakdown.filter((d: any) => d.percent > 0);
              return (
                <>
                  <Tooltip target={`.dist-bar-${rowData.scenario_id}`} position="top" mouseTrack mouseTrackTop={10}>
                    <div style={{ minWidth: '160px', padding: '2px 0' }}>
                      {visibleDist.map((d: any) => (
                        <div key={d.family} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '2px 0' }}>
                          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, backgroundColor: d.color, border: '1px solid rgba(0,0,0,0.1)', flexShrink: 0 }} />
                          <span style={{ flex: 1 }}>{d.family}</span>
                          <span style={{ fontWeight: 600, marginLeft: 8 }}>{Math.round(d.percent * 100)}%</span>
                        </div>
                      ))}
                    </div>
                  </Tooltip>
                  <div
                    className={`dist-bar-${rowData.scenario_id}`}
                    style={{
                      display: 'flex',
                      width: '80%',
                      height: '14px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      backgroundColor: '#e5e7eb',
                      cursor: 'pointer'
                    }}
                  >
                    {rowData.distributionBreakdown.map((dist: any) => (
                      <div
                        key={dist.family}
                        style={{
                          width: `${dist.percent * 100}%`,
                          backgroundColor: dist.color
                        }}
                      />
                    ))}
                  </div>
                </>
              );
            }}
            style={{ minWidth: '140px' }}
          />
          {ncrMode === 'medicare' ? (
            <Column
              field="totalMedicareReimb"
              header={`${ncrLabel} Reimb.`}
              body={(rowData) => formatCurrency(rowData.totalMedicareReimb)}
              sortable
              sortFunction={pinnedSort}
              style={{ minWidth: '160px' }}
            />
          ) : (
            <Column
              field="totalPvtReimb"
              header="Weighted Reimb."
              body={(rowData) => formatCurrency(rowData.totalPvtReimb)}
              sortable
              sortFunction={pinnedSort}
              style={{ minWidth: '160px' }}
            />
          )}
          <Column
            field="totalPrice"
            header="Price"
            body={(rowData) => formatCurrency(rowData.totalPrice)}
            sortable
            sortFunction={pinnedSort}
            style={{ minWidth: '120px' }}
          />
          <Column
            field="totalRebate"
            header="Rebate"
            body={(rowData) => formatCurrency(rowData.totalRebate)}
            sortable
            sortFunction={pinnedSort}
            style={{ minWidth: '120px' }}
          />
          {ncrMode === 'medicare' ? (
            <Column
              field="totalNCR"
              header={` ${ncrLabel} NCR`}
              body={(rowData) => formatCurrency(rowData.totalNCR)}
              sortable
              sortFunction={pinnedSort}
              style={{ minWidth: '140px' }}
            />
          ) : (
            <Column
              field="totalWeightedNCR"
              header="Weighted NCR"
              body={(rowData) => formatCurrency(rowData.totalWeightedNCR)}
              sortable
              sortFunction={pinnedSort}
              style={{ minWidth: '160px' }}
            />
          )}
        </DataTable>

      </Card>

      <RebateDetailDialog
        visible={rebateDetailVisible}
        onHide={() => setRebateDetailVisible(false)}
        onExited={() => setRebateDetailTarget(null)}
        target={rebateDetailTarget}
        rebateDetailRows={rebateDetailRows}
        summaryRow={rebateDetailTarget ? state.scenarioResults.find(r =>
          Number(r.scenario_id) === rebateDetailTarget.scenarioId &&
          String(r.product_family_id) === String(rebateDetailTarget.pfId)
        ) : undefined}
      />

      <Dialog
        header="Decision logic"
        visible={isDecisionLogicModalVisible}
        onHide={closeDecisionLogicModal}
        transitionOptions={{ timeout: { enter: 150, exit: 150 }, onExited: resetDecisionLogicModal }}
        style={{ width: '100vw', maxWidth: '1100px' }}
        breakpoints={{ '960px': '100vw' }}
        modal
        dismissableMask={true}
        blockScroll
        contentStyle={{ maxHeight: '80vh', overflow: 'auto' }}
      >
        {decisionLogicScenario ? renderDecisionLogicContent(decisionLogicScenario) : null}
      </Dialog>

      <Dialog
        header="Alternate scenario"
        visible={isLegacyAlternateModalVisible}
        onHide={closeLegacyAlternateModal}
        style={{ width: '100vw', maxWidth: '1100px' }}
        breakpoints={{ '960px': '100vw' }}
        modal
        dismissableMask={true}
        blockScroll
        contentStyle={{ maxHeight: '80vh', overflow: 'auto' }}
      >
        {renderLegacyAlternateContent()}
      </Dialog>

      {isComparisonModalVisible && (
        <Dialog
          header="Scenario Comparison"
          visible={isComparisonModalVisible}
          onHide={() => setComparisonModalVisible(false)}
          // The cards render from selectedForComparison, so it outlives the closing animation.
          transitionOptions={{ timeout: { enter: 150, exit: 150 }, onExited: () => setSelectedForComparison([]) }}
          style={{ width: '98vw', maxWidth: '98vw' }}
          breakpoints={{ '960px': '98vw' }}
          modal
          dismissableMask={true}
          blockScroll
          contentStyle={{ padding: '0 0 1rem 0', background: '#f8f9fa', overflowX: 'auto' }}
          headerStyle={{ paddingTop: '1rem', paddingBottom: '0.25rem', background: '#f8f9fa' }}
        >
          <div className={`comparison-container ${
            selectedForComparison.length > 2
              ? 'grid-comparison'
              : ''
          }`}>
            {selectedForComparison.map((scenario, idx) => {
              if (!scenario) return null; // safety check

              return (
                <div
                  key={scenario.scenario_id}
                  className="comparison-card"
                >
                  <h4
                    className="m-0 mb-1 text-lg font-semibold text-gray-800"
                    style={{ lineHeight: 1.2 }}
                  >
                    {getScenarioDisplayLabel(Number(scenario.scenario_id))}
                  </h4>

                  <div
                    className="p-3"
                    style={{
                      paddingLeft: '0.5rem',
                      paddingRight: '0.5rem',
                      marginBottom: 0,
                      paddingBottom: 0,
                    }}
                  >
                    <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                      {ncrMode === 'medicare' ? (
                        <div><strong>{ncrLabel} Reimb.:</strong> {formatCurrency(scenario.totalMedicareReimb)}</div>
                      ) : (
                        <div><strong>Weighted Reimb.:</strong> {formatCurrency(scenario.totalPvtReimb)}</div>
                      )}
                      <div><strong>Price:</strong> {formatCurrency(scenario.totalPrice)}</div>
                      <div><strong>Rebate:</strong> {formatCurrency(scenario.totalRebate)}</div>
                      {ncrMode === 'medicare' ? (
                        <div><strong>{ncrLabel} NCR:</strong> {formatCurrency(scenario.totalNCR)}</div>
                      ) : (
                        <div><strong>Weighted NCR:</strong> {formatCurrency(scenario.totalWeightedNCR)}</div>
                      )}

                    </div>
                  </div>

                  {renderRowExpansion(scenario, { hideActions: true })}
                </div>
              );
            })}
          </div>
        </Dialog>


      )}

      <div style={{ marginTop: '2rem', padding: '1rem 1.5rem', borderTop: '1px solid var(--surface-border)', fontSize: '0.72rem', color: 'var(--text-color-secondary)', lineHeight: '1.6' }}>
        <p><b>Ranked by selected criteria; verify before acting.</b> ScenarioIQ rankings are informational scenario comparisons only and are based on available data, selected assumptions, and modeling logic that may be incomplete, outdated, or subject to change. Rankings should not be interpreted as determining, recommending, implying, or directing clinical appropriateness, medical necessity, payer coverage, or which drug should be prescribed, ordered, purchased, dispensed, routed, referred, used, or recommended. Confirm payer requirements, contract terms, formulary status, and patient/practice-specific facts before taking action.</p>
      </div>

    </div>
  );
};

export default ScenarioResults;
